import {
    AppStoreArtifactUploadDestinationTypeOption,
    Nullable,
    QuickStartCISetupStepType,
    StringKeyedDictionary,
    UserAppData,
} from '@/typescriptHelpers';
import {
    AppCenterBetaIntegrationData,
    AppDevice,
    AppDistributionService,
    AppDistributionServiceIntegrationTypes,
    AppEntity,
    AppGroup,
    AppHome,
    AppIdentifierForIntegration,
    AppIdentifiersAndPlatform,
    AppInfo,
    AppleAppStoreData,
    AppleIntegrationData,
    AppMonitoringHealthMetric,
    AppMonitoringHealthMetricCategory,
    AppNotification,
    AppNotificationType,
    AppPlatform,
    AppSettings,
    AppStoreArtifactUploadDestinationType,
    AppStoreDefaultReleaseNotes,
    AppStoreDefaultSettings,
    AppStoreType,
    AuthCredentials,
    Automation,
    AutomationApplyReviewAttachment,
    AutomationRun,
    AutomationType,
    BranchConfig,
    BuildDistributionBucket,
    BuildDistroBucketDownloadMetrics,
    BuildDistroBuild,
    BuildDistroNotificationType,
    ChecklistItem,
    ChecklistItemTemplate,
    CommonNotification,
    CustomMessagesConfig,
    DefaultUserGroupId,
    Device,
    DisabledOrLimitedSubscriptionFeature,
    Environment,
    FeatureAffiliationConfig,
    FeatureFlag,
    FeatureItem,
    FeatureItemCodeStatus,
    FileStats,
    FirebaseBetaIntegrationData,
    GoogleBetaIntegrationData,
    GoogleIntegrationData,
    Integration,
    IntegrationAmazon,
    IntegrationApple,
    IntegrationCapability,
    IntegrationGoogle,
    IntegrationHuawei,
    IntegrationInstallationId,
    IntegrationProvider,
    IntegrationSamsung,
    IntegrationType,
    LifecycleFreezeSet,
    MergeType,
    OrganizationEntity,
    OrgOverviewApprovalItemDurations,
    OrgOverviewStatistic,
    OrgSettings,
    OrgSubscriptionInfo,
    OrgUser,
    ProvisioningProfile,
    Release,
    ReleaseCreatedOrigin,
    ReleaseOverview,
    ReleaseOverviewStats,
    ReleasePilotRotation,
    ReleaseScheduleCadenceSettings,
    ReleaseStatistics,
    ReleaseStatus,
    ReleaseStep,
    ReleaseStepApprovalsData,
    ReleaseStepBlockingConfig,
    ReleaseStepFeatureReadinessData,
    ReleaseStepKickoff,
    ReleaseStepRegressionTestingData,
    ReleaseStepSubmission,
    ReleaseStepTakeoff,
    ReleaseStepTakeoffData,
    ReleaseStepType,
    ReleaseTag,
    ReleaseTimelinePhase,
    ReleaseType,
    ReleaseTypesForNonBlockingSteps,
    RolloutMonitoring,
    ServerType,
    SigningKey,
    SubscriptionFeaturesAndCapabilities,
    SubscriptionFeatureType,
    SubscriptionStatus,
    TagConfig,
    TargetVersionInfo,
    TestFlightIntegrationData,
    TimelineEvent,
    User,
    UserEmailNotificationSettings,
    UserGroup,
    UserGroupsToCommsGroupsMap,
    UserPermissions,
    WebhookEndpoint,
    WorkflowEnabledSteps,
} from '@/types';
import * as Logger from '@/utils/logger';
import { error } from '@/utils/logger';
import {
    allSupportedIntegrationTypesForCapability,
    automationIsBlocked,
    automationIsCompleted,
    automationIsEnabledAndActiveAndScheduled,
    automationIsFailed,
    betaIntegrationTypeIsAppStore,
    codeIsCommit,
    compareVersions,
    computeCompletedChecklistItemsCount,
    displayNameForCapability,
    filterTrackByPlatform,
    getAllAppsInOrg,
    getAllReleasesForAppId,
    getIsAppleAppStore,
    getIsApplePlatform,
    getReleaseTypeForNonBlockingSteps,
    getTestingTrackFromAutoPromoteBuildToTestTrackAutomationWithState,
    isAppPlatform,
    isPhasedReleasePaused,
    recommendedCapabilitiesForReleaseStepType,
    requiredCapabilitiesForPlatform,
    userIsRunwayAdmin,
    userIsRunwayAdminReadOnly,
} from '@/helpers';
import { RunwayUserAction } from '@/helpers/aclHelpers';
import { RootState } from '@/reducers/reducers';
import { DEFAULT_TRIAGE_FILTERS } from '@/reducers/triageFilters';
import * as Storage from '../helpers/localStorageHelper';
import { QuickStartCISetupStepData } from '@/helpers/localStorageHelper';
import { cloneDeep, compact, Dictionary, every, flatMap, forEach, groupBy, isNil, keyBy, reduce, uniq } from 'lodash';
import { AnyFilter } from '@/types/filters';
import { ApprovalsSortType, AutomationsToolboxSortType, FeatureFlagsSortType, SortType } from '@/types/sorts';
import { allReleasePropertiesFrom } from '@/helpers/trackingHelpers';
import { displayNameForIntegrationType, integrationCapabilitiesForPlatform } from '@/helpers/integrationHelpers';
import { displaySubscriptionStatus, displayUserNameLong } from '@/helpers/displayHelpers';
import { TEST_ORG_IDS } from '@/constants';

import { FeatureWorkItems, generateBackwardsCompatibleReleaseStepRunId } from '@/reducers/releaseStep';
import { FetchableEntityTypeV2 } from '@/entityType';
import { CommonSagaActionsData } from '@/actions/entities';
import { StepArchetypeGroup, WorkflowRunStepRunGraph } from '@/types/workflowRunStepRunGraph';
import { StepArchetype, StepRun } from '@/types/stepRun';
import { StepConfig } from '@/types/stepConfig';
import { StepGraphStep, WorkflowStepGraph } from '@/types/workflow';
import { AnyTimelineEventsFilter, TimelineEventsFilterType } from '@/types/timelineEventsFilters';
import { dayjs } from '@/extendedDayjs';
import { Destination } from '@/types/destinations';
import { WorkflowOverview } from '@/types/workflowOverview';
import { RolloutStepRunStatusData, StepRunStatusData } from '@/types/stepRunStatus';
import { TimezonePreferenceEnum } from '@/components/userSettings/EditTimezoneSettingsModal';

const _uiConfig = (state: RootState): any => (state.ui as any).uiConfig;

export const selectHasFeatureManagementIntegration: (state: RootState, appId: string) => boolean = (
    state: RootState,
    appId: string,
) => {
    const integration = selectFeatureManagementIntegration(state, appId);

    return !!integration;
};
export const selectHasFeatureWorkItemsForStepRunId: (state: RootState, stepRunId: string) => boolean = (
    state,
    stepRunId,
) => {
    // return demo feature work items if enabled
    if (
        _uiConfig(state).demoData &&
        _uiConfig(state).demoData.releaseStepsDemoData &&
        _uiConfig(state).demoData.releaseStepsDemoData[stepRunId]?.releaseStep?.data
    ) {
        return true;
    }

    const workItems = selectFeatureWorkItemsForStepRunId(state, stepRunId);

    return !!workItems;
};

export const selectIsDemoModeEnabledForRelease: (state: RootState, releaseId: string) => boolean | undefined = (
    state,
    releaseId,
) => {
    return (
        _uiConfig(state).demoData &&
        _uiConfig(state).demoData.releaseDemoData &&
        !!_uiConfig(state).demoData.releaseDemoData[releaseId]
    );
};

export const selectIsDemoInstanceOrDemoModeEnabled: (state: RootState, orgId: string, releaseId?: string) => boolean = (
    state,
    orgId,
    releaseId,
) => {
    const isDemoInstance = selectIsDemoInstance(state, orgId);
    const isAppsDemoModeEnabled = selectIsAppsDemoModeEnabled(state);
    let isReleaseDemoModeEnabled = false;

    if (releaseId) {
        isReleaseDemoModeEnabled = !!selectIsDemoModeEnabledForRelease(state, releaseId);
    }

    return isDemoInstance || isAppsDemoModeEnabled || isReleaseDemoModeEnabled;
};

export const selectIsAppsDemoModeEnabled: (state: RootState) => boolean = (state) => {
    if (_uiConfig(state)?.demoData?.appsDemoData) {
        return true;
    }

    return false;
};

export const selectIsAppStateFetchedForApps: (state: RootState) => Record<string, boolean> = (state) => {
    const allAppIds = selectAllAppIds(state);
    const isAppStateFetchedForApps: Record<string, boolean> = {};

    forEach(allAppIds, (appId) => {
        isAppStateFetchedForApps[appId] = selectIsAppStateFetched(state, appId);
    });

    return isAppStateFetchedForApps;
};

export const selectQuickStartCIStepData: <T extends QuickStartCISetupStepType>(
    state: RootState,
    appId: string,
    stepType: T,
) => QuickStartCISetupStepData<T> = (state, appId, stepType) => {
    return _uiConfig(state).quickStartCIStepsData[`${appId}#${stepType}`] as QuickStartCISetupStepData<typeof stepType>;
};

export const selectSubmittedRollback: (state: RootState, appId: string) => Release | null = (state, appId) => {
    const rollbacks = getAllReleasesForAppId(appId, state).filter(
        (release) => release.type === ReleaseType.Rollback && release.timelinePhase === ReleaseTimelinePhase.Rollback,
    );

    if (rollbacks.length > 0) {
        const rollback = rollbacks[0]; // there should only ever be one

        return rollback.isSubmitted ? rollback : null;
    }

    return null;
};

export const selectWorkingBranch: (state: RootState, appId: string) => string | undefined = (state, appId) => {
    const appSettings = selectAppSettings(state, appId);

    return appSettings.workingBranch;
};

export const selectIsReleaseCompleted: (state: RootState, releaseId: string) => boolean = (state, releaseId) => {
    const release = selectRelease(state, releaseId);

    return release.timelinePhase == ReleaseTimelinePhase.Completed;
};

export const selectReleaseVersion: (state: RootState, releaseId: string) => string = (state, releaseId) => {
    const release = selectRelease(state, releaseId);

    return release.version;
};

export const selectReleaseDisplayableVersion: (state: RootState, releaseId: string) => string = (state, releaseId) => {
    const release = selectRelease(state, releaseId);

    return release.displayableVersion;
};

/*
This is a workaround for a bizarre bug with reselect where it can't handle two selectors
with the same type arguments that are meant to be different arguments (e.g releaseId and appId, both string type)
*/
export const selectFeatureWorkItemsForAppIdReleaseId: (
    state: RootState,
    appId: string,
    releaseId: string,
) => FeatureItem[] = (state, appId, releaseId) => {
    const items = selectAllReleaseBranchWorkItems(state, releaseId);

    if (items) {
        return items;
    }

    return [];
};

export const selectFilters: (state: RootState) => AnyFilter[] = (state) => {
    return (state.ui as any).featureItemSearchAndFiltersAndSorts.filters;
};

export const selectSort: (state: RootState) => SortType = (state) => {
    return (state.ui as any).featureItemSearchAndFiltersAndSorts.sort;
};

export const selectSearch: (state: RootState) => string | undefined = (state) => {
    return (state.ui as any).featureItemSearchAndFiltersAndSorts.query;
};

export const selectAutomationsToolboxSort: (state: RootState) => AutomationsToolboxSortType = (state) => {
    return (state.ui as any).automationsToolboxSorts.sort;
};

export const selectFeatureFlagsSort: (state: RootState) => FeatureFlagsSortType = (state) => {
    return (state.ui as any).featureFlagsSorts.sort;
};

export const selectApprovalsSort: (state: RootState) => ApprovalsSortType = (state) => {
    return (state.ui as any).approvalsSearchAndSorts.sort;
};

export const selectApprovalsSearch: (state: RootState) => string = (state) => {
    return (state.ui as any).approvalsSearchAndSorts.query;
};

export const selectFeatureFlags: (state: RootState, releaseId: string) => FeatureFlag[] | undefined = (
    state,
    releaseId,
) => {
    //return demo feature work items if enabled
    if (
        _uiConfig(state).demoData &&
        _uiConfig(state).demoData.releaseDemoData &&
        _uiConfig(state).demoData.releaseDemoData[releaseId]
    ) {
        return _uiConfig(state).demoData.releaseDemoData[releaseId].release.featureFlags;
    }

    const release = selectRelease(state, releaseId);
    const featureFlags = release.featureFlags;

    if (featureFlags) {
        return featureFlags;
    } else {
        return [];
    }
};

export const selectApprovalItems: (
    state: RootState,
    releaseId: string,
    stepRunId: Nullable<string>,
) => ChecklistItem[] = (state, releaseId, stepRunId) => {
    const releaseStep = selectReleaseStep(state, releaseId, ReleaseStepType.Approvals);
    const stepRun = stepRunId ? selectStepRun(state, stepRunId) : undefined;
    const stepData: ReleaseStepApprovalsData =
        stepRun ? { ...stepRun.data.summaryData, ...stepRun.data.complementaryData } : releaseStep.data;
    const approvalItems = stepData.approvalItems;

    if (approvalItems) {
        return approvalItems;
    } else {
        return [];
    }
};

export const selectRegressionItems: (
    state: RootState,
    releaseId: string,
    stepRunId: Nullable<string>,
) => ChecklistItem[] = (state, releaseId, stepRunId) => {
    const releaseStep = selectReleaseStep(state, releaseId, ReleaseStepType.RegressionTesting);
    const stepRun = stepRunId ? selectStepRun(state, stepRunId) : undefined;
    const stepData: ReleaseStepRegressionTestingData =
        stepRun ? { ...stepRun.data.summaryData, ...stepRun.data.complementaryData } : releaseStep.data;
    const items = stepData.testItems;

    if (items) {
        return items;
    } else {
        return [];
    }
};

export const selectApprovalItem: (
    state: RootState,
    releaseId: string,
    checklistItemId: string,
    stepRunId: Nullable<string>,
) => ChecklistItem | undefined = (state, releaseId, checklistItemId, stepRunId) => {
    const items = selectApprovalItems(state, releaseId, stepRunId);
    return items.find((item) => item.id === checklistItemId);
};

export const selectRegressionItem: (
    state: RootState,
    releaseId: string,
    checklistItemId: string,
    stepRunId: Nullable<string>,
) => ChecklistItem | undefined = (state, releaseId, checklistItemId, stepRunId) => {
    const items = selectRegressionItems(state, releaseId, stepRunId);
    return items.find((item) => item.id === checklistItemId);
};

export const selectUsersSearch: (state: RootState) => string = (state) => {
    return (state.ui as any).userSearch.query;
};

export const selectFeatureWorkItemsForStepRunId: (state: RootState, stepRunId: string) => FeatureWorkItems = (
    state,
    stepRunId,
) => {
    const featureReadinessReleaseStep = selectReleaseStep(state, '', null as unknown as ReleaseStepType, stepRunId);
    const featureReadinessReleaseStepData = featureReadinessReleaseStep?.data as ReleaseStepFeatureReadinessData;
    if (featureReadinessReleaseStepData) {
        const byId = keyBy(featureReadinessReleaseStepData.items, 'id');
        const workingBranchItemsById = keyBy(featureReadinessReleaseStepData.workingBranchItems, 'id');

        return {
            byId,
            workingBranchItemsById,
        };
    }
    return {
        byId: {},
        workingBranchItemsById: {},
    };
};

export const selectFeatureWorkItemsCustomTicketFields: (state: RootState, stepRunId: string) => string[] = (
    state,
    stepRunId,
) => {
    const featureReadinessReleaseStep = selectReleaseStep(state, '', null as unknown as ReleaseStepType, stepRunId);
    const featureReadinessReleaseStepData = featureReadinessReleaseStep?.data as ReleaseStepFeatureReadinessData;
    return featureReadinessReleaseStepData?.customTicketFields ?? [];
};

export const selectFeatureWorkItem: (
    state: RootState,
    stepRunId: string,
    featureWorkItemId: string,
) => FeatureItem | undefined = (state, stepRunId, featureWorkItemId) => {
    const allWorkItemsForRelease = selectAllFeatureWorkItems(state, stepRunId);

    return allWorkItemsForRelease.find((item) => item.id === featureWorkItemId);
};

export const selectPublicApiKey: (state: RootState) => string = (state) => {
    return (state.ui as any).publicApiKey.keyValue;
};

export const selectAllWorkingBranchWorkItems: (state: RootState, stepRunId: string) => FeatureItem[] = (
    state,
    stepRunId,
) => {
    const workItemsForRelease = selectFeatureWorkItemsForStepRunId(state, stepRunId);

    if (workItemsForRelease) {
        return Object.values(workItemsForRelease.workingBranchItemsById);
    }

    return [];
};

export const selectAllReleaseBranchWorkItems: (state: RootState, stepRunId: string) => FeatureItem[] = (
    state,
    stepRunId,
) => {
    const workItemsForRelease = selectFeatureWorkItemsForStepRunId(state, stepRunId);

    if (workItemsForRelease) {
        return Object.values(workItemsForRelease.byId);
    }

    return [];
};

// both against release branch and working branch, only unique work items
export const selectAllFeatureWorkItems: (state: RootState, stepRunId: string) => FeatureItem[] = (state, stepRunId) => {
    const hasFeatureWorkItemsForRelease = selectHasFeatureWorkItemsForStepRunId(state, stepRunId);
    if (hasFeatureWorkItemsForRelease) {
        const workingBranchItems = selectAllWorkingBranchWorkItems(state, stepRunId);
        const releaseBranchItems = selectAllReleaseBranchWorkItems(state, stepRunId);

        return uniq(workingBranchItems.concat(releaseBranchItems));
    }

    return [];
};

export const selectFeatureWorkItems: (
    state: RootState,
    stepRunId: string,
    useWorkingBranch: boolean,
) => FeatureItem[] = (state, stepRunId, useWorkingBranch) => {
    const hasFeatureWorkItemsForRelease = selectHasFeatureWorkItemsForStepRunId(state, stepRunId);

    if (hasFeatureWorkItemsForRelease) {
        if (useWorkingBranch) {
            return selectAllWorkingBranchWorkItems(state, stepRunId);
        }

        return selectAllReleaseBranchWorkItems(state, stepRunId);
    }

    return [];
};

export const selectIsReleaseKickedOff: (state: RootState, releaseId: string) => boolean = (state, releaseId) => {
    const release = selectRelease(state, releaseId);

    return release.isKickedOff;
};

export const selectIsReleaseSubmitted: (state: RootState, releaseId: string) => boolean = (state, releaseId) => {
    const release = selectRelease(state, releaseId);

    return release.isSubmitted;
};

export const selectIsReleaseReleased: (state: RootState, releaseId: string) => boolean = (state, releaseId) => {
    const release = selectRelease(state, releaseId);

    return release.isReleased;
};

export const selectAppNeedsIntegrationsSetup: (state: RootState, appId: string) => boolean = (state, appId) => {
    const hasRequiredCapabilities = selectAppHasRequiredCapabilitiesConfigured(state, appId);

    return !hasRequiredCapabilities;
};

export const selectAppGroupIdForApp: (state: RootState, appId: string) => string | undefined = (state, appId) => {
    if (_uiConfig(state)?.demoData?.appGroupsDemoData?.length) {
        // all demo apps are in the same app group, so can just return that app group id
        return _uiConfig(state).demoData.appGroupsDemoData.find((group) => group.appIds.includes(appId))?.id;
    }

    return state.relationships.appGroupForApp[appId]?.appGroupId;
};

export const selectWasAppCreatedAfterQualitativeOnboarding: (state: RootState, appId: string) => boolean | undefined = (
    state,
    appId,
) => {
    const MIN_PERIOD_DATE = new Date('2022-06-10');
    const THIRTY_DAYS_IN_MS = 30 * 24 * 60 * 60 * 1000;

    const app = selectApp(state, appId);

    return !!app.createdAt && new Date(app.createdAt).getTime() - MIN_PERIOD_DATE.getTime() > -THIRTY_DAYS_IN_MS;
};

export const selectAuthCredentials: (state: RootState) => AuthCredentials | undefined = (state) => {
    return state.auth.authCredentials;
};

export const selectStepRunGraph: (state: RootState, workflowRunId: string) => WorkflowRunStepRunGraph = (
    state,
    workflowId,
) => {
    const release = selectRelease(state, workflowId);
    return release?.stepRunGraph;
};

export const selectRolloutStepRunStatusDataInStepRunGraph: (
    state: RootState,
    releaseId: string,
) => RolloutStepRunStatusData[] = (state, releaseId) => {
    const stepRunGraph = selectStepRunGraph(state, releaseId);
    return flatMap(stepRunGraph.stepGroups, (stepGroup) =>
        flatMap(stepGroup.stepArchetypeGroups, (stepArchetypeGroup) =>
            flatMap(stepArchetypeGroup.stepRunStatusDatum, (stepRunStatusData) => {
                if (stepRunStatusData.type === ReleaseStepType.Rollout) {
                    return stepRunStatusData as RolloutStepRunStatusData;
                }
                return [];
            }),
        ),
    ) as RolloutStepRunStatusData[];
};

export const selectStepGraph: (state: RootState, appId: string) => WorkflowStepGraph = (state, appId) => {
    const appSettings = selectAppSettings(state, appId);
    return appSettings?.stepGraph;
};

export const selectStepConfigDataFromStepGraphs: (state: RootState, appId: string) => Record<string, StepGraphStep> = (
    state,
    appId,
) => {
    const stepGraph = selectStepGraph(state, appId);

    const stepConfigsMap: Record<string, StepGraphStep> = {};
    stepGraph?.stepGroups.forEach((stepGroup) => {
        stepGroup.archetypeGroups.forEach((archetypeGroup) => {
            archetypeGroup.steps.forEach((step) => {
                stepConfigsMap[step.stepConfigId] = step;
            });
        });
    });

    return stepConfigsMap;
};

export const selectRelease: (state: RootState, releaseId: string) => Release = (state, releaseId) => {
    if (_uiConfig(state)?.demoData?.releaseDemoData?.[releaseId]) {
        return _uiConfig(state).demoData.releaseDemoData[releaseId]?.release;
    }

    const { entities } = state;
    return entities.releases.byId[releaseId];
};

export const selectIsReleaseRollback: (state: RootState, releaseeId: string) => boolean = (state, releaseId) => {
    const release = selectRelease(state, releaseId);
    return release.type === ReleaseType.Rollback;
};

export const selectIsReleaseFetched: (state: RootState, releaseId: string) => boolean = (state, releaseId) => {
    const release = selectRelease(state, releaseId);
    return !!release?.createdAt;
};

export const selectShouldWarnAboutNewerCommits: (state: RootState, releaseId: string) => boolean = (
    state,
    releaseId,
) => {
    const release = selectRelease(state, releaseId);
    return release?.shouldWarnAboutNewerCommits ?? false;
};

export const selectIsAppStateFetched: (state: RootState, appId: string) => boolean = (state, appId) => {
    const app = selectApp(state, appId);

    return !!app?.createdAt; // arbitrary property that exists on the AppEntity / App model, but not the AppList model.
};

export const selectIsReleaseStepPreviouslyFetched: (
    state: RootState,
    releaseId: string,
    releaseStepType: ReleaseStepType,
) => boolean = (state, releaseId, releaseStepType) => {
    const releaseStep = selectReleaseStep(state, releaseId, releaseStepType);
    return !!releaseStep;
};

export const selectIsStepRunPreviouslyFetched: (state: RootState, stepRunId: string) => boolean = (
    state,
    stepRunId,
) => {
    const stepRun = selectStepRun(state, stepRunId);
    return !!stepRun;
};

export const selectIsWorkflowOverviewPreviouslyFetchedForApp: (state: RootState, appId: string) => boolean = (
    state,
    appId,
) => {
    const workflowOverview = selectWorkflowOverview(state, appId);
    return !!workflowOverview;
};

export const selectReleaseCount: (state: RootState) => number = (state) => {
    const { entities } = state;

    return entities.releases.allIds.length;
};

export const selectReleaseType: (state: RootState, releaseId: string) => ReleaseType = (state, releaseId) => {
    const release = selectRelease(state, releaseId);

    return release.type;
};

export const selectRollbackTargetVersionInfo: (state: RootState, releaseId: string) => TargetVersionInfo | undefined = (
    state,
    releaseId,
) => {
    const release = selectRelease(state, releaseId);
    return release.rollbackTargetVersionInfo;
};

export const selectOrg: (state: RootState, orgId?: string) => OrganizationEntity | null | undefined = (
    state: RootState,
    orgId?: string,
) => {
    const { entities } = state;

    if (orgId) {
        return entities.orgs.byId[orgId];
    } else {
        const firstOrgId = entities.orgs.allIds.length > 0 ? entities.orgs.allIds[0] : null;

        if (firstOrgId) {
            return entities.orgs.byId[firstOrgId];
        }

        return null;
    }
};

export const selectIsPlayReleaseLifecycleEnabled: (state: RootState, orgId?: string) => boolean = (state, orgId) => {
    return selectOrg(state, orgId)?.enablePlayReleaseLifecycle ?? false;
};

export const selectOrgSettings: (state: RootState, orgId?: string) => OrgSettings = (state, orgId) => {
    const org = selectOrg(state, orgId)!;

    return org.orgSettings;
};

export const selectApp: (state: RootState, appId: string) => AppEntity = (state, appId) => {
    if (_uiConfig(state)?.demoData?.appsDemoData?.[appId]) {
        return _uiConfig(state).demoData.appsDemoData[appId].app;
    }

    const { entities } = state;
    return entities.apps.byId[appId];
};

export const selectAppHome: (state: RootState, appId: string) => AppHome | undefined = (state, appId) => {
    const app = selectApp(state, appId);
    return app?.appHome;
};

export const selectEffectiveTimezone = (state: RootState, appId: string) => {
    // User preference is now prioritized over app-level setting
    const localTzName = dayjs.tz.guess();

    // Prefer the org-level user (OrgUser) when available. Fall back to state.user.
    const org = selectOrg(state)!;
    const userId = state?.user?.id;
    const orgUser = org && userId ? selectOrgUser(state, org.id, userId) : undefined;
    const timezonePref = orgUser?.timezonePreference ?? state.user?.timezonePreference;

    switch (timezonePref) {
        case TimezonePreferenceEnum.Local:
            return localTzName || 'UTC';
        case TimezonePreferenceEnum.OrgWide:
            return org?.timezone || 'UTC';
        case TimezonePreferenceEnum.ScheduleSettings:
        default: {
            const app = selectApp(state, appId);
            return app?.appSettings?.releaseScheduleCadenceSettings?.cadenceTimezone || 'UTC';
        }
    }
};

export const selectCadenceTimezone = (state: RootState, appId: string) => {
    const app = selectApp(state, appId);
    return app?.appSettings?.releaseScheduleCadenceSettings?.cadenceTimezone;
};

export const selectDemoApp = (state: RootState, appId: string) => {
    return _uiConfig(state)?.demoData?.appsDemoData?.[appId]?.app;
};

export const selectAllAppIds: (state: RootState) => string[] = (state) => {
    if (_uiConfig(state).demoData?.appsDemoData) {
        return Object.keys(_uiConfig(state).demoData.appsDemoData);
    }

    return state.entities.apps.allIds;
};

export const selectAllAppNamesAndPlatformsByAppId: (state: RootState) => Record<string, AppInfo> = (state) => {
    if (_uiConfig(state)?.demoData?.appsDemoData) {
        return Object.entries(_uiConfig(state).demoData.appsDemoData).reduce(
            (acc, [appId, appDemoData]: [string, any]) => {
                acc[appId] = {
                    name: appDemoData.app.appSettings.appName,
                    platform: appDemoData.app.appSettings.platform,
                    appStoreType: appDemoData.app.appSettings.appStoreType,
                };
                return acc;
            },
            {} as Record<string, AppInfo>,
        );
    }

    return state.entities.apps.allIds.reduce((appNamesByAppId, appId) => {
        const appName = selectAppName(state, appId);
        const platform = selectAppPlatform(state, appId);
        const appStoreType = selectAppStoreType(state, appId);
        return {
            ...appNamesByAppId,
            [appId]: {
                name: appName,
                platform,
                appStoreType,
            },
        };
    }, {});
};

export const selectAllAppIdsInAppGroup: (state: RootState, appGroupId: string) => string[] = (state, appGroupId) => {
    const appGroup = selectAppGroup(state, appGroupId);

    return appGroup?.appIds ?? [];
};

export const selectAppCount: (state: RootState) => number = (state) => {
    const org = selectOrg(state)!;

    if (org) {
        const appIds = selectAllAppsIdsInOrg(state, org.id);

        return appIds.length;
    }

    return 0;
};

export const selectAllAppsIdsInOrg: (state: RootState, orgId: string) => string[] = (state, orgId) => {
    if (_uiConfig(state).demoData && _uiConfig(state).demoData.appsDemoData) {
        return Object.keys(_uiConfig(state).demoData.appsDemoData);
    }

    return state.relationships.appsInOrg[orgId].appIds;
};

export const selectAppGroup: (state: RootState, appGroupId: string) => AppGroup | undefined = (state, appGroupId) => {
    if (_uiConfig(state)?.demoData?.appGroupsDemoData?.length) {
        return _uiConfig(state).demoData.appGroupsDemoData.find((group) => group.id === appGroupId);
    }

    return state.entities.appGroups.byId[appGroupId];
};

// Warning! Do not use - use selectAppsInGroup in memoizedSelectors instead, apps can be undefined here
export const selectAppsInAppGroup: (state: RootState, appGroupId: string) => AppEntity[] = (state, appGroupId) => {
    const appGroup = selectAppGroup(state, appGroupId);
    return appGroup?.appIds.map((appId) => selectApp(state, appId)) ?? [];
};

export const selectAllAppGroupNamesInOrg: (state: RootState, orgId: string) => string[] = (state, orgId) => {
    const appGroupsForOrg = state.relationships.appGroupsInOrg[orgId];

    if (appGroupsForOrg) {
        return appGroupsForOrg.appGroupIds.map((appGroupId) => {
            const appGroup = selectAppGroup(state, appGroupId);

            return appGroup?.name ?? '';
        });
    }

    return [];
};

export const selectReleaseDefaults: (state: RootState, appId: string) => AppStoreDefaultSettings | undefined = (
    state,
    appId,
) => {
    const app = selectApp(state, appId);

    return app.appSettings.appStoreDefaults;
};

export const selectCustomMessagesConfig: (state: RootState, appId: string) => CustomMessagesConfig = (state, appId) => {
    const app = selectApp(state, appId);

    return app.appSettings.customMessagesConfig;
};

export const selectDefaultReleaseStepsForApp: (state: RootState, appId: string) => ReleaseStepType[] = (
    state,
    appId,
) => {
    const releaseStepsByReleaseType = selectReleaseStepsByReleaseType(state, appId);
    return releaseStepsByReleaseType[ReleaseType.Minor] ?? [];
};

export const selectDefaultReleaseStepsForAppForChecklists: (state: RootState, appId: string) => ReleaseStepType[] = (
    state,
    appId,
) => {
    const releaseStepsByReleaseTypeForApp = selectDefaultReleaseStepsForApp(state, appId);
    return [...releaseStepsByReleaseTypeForApp, ReleaseStepType.Rollout];
};

export const selectReleaseStepsByReleaseType: (
    state: RootState,
    appId: string,
) => StringKeyedDictionary<ReleaseStepType[]> = (state, appId) => {
    const app = selectApp(state, appId);
    return app?.releaseStepArchetypes ?? {};
};

export const selectIsBuildDistroEnabled: (state: RootState, appId: string) => boolean = (state, appId) => {
    if (_uiConfig(state).demoData?.buildDistroDemoData) {
        return true;
    }

    const app = selectApp(state, appId);
    if (!app?.appSettings) {
        return false;
    }

    return !!app.appSettings.buildDistributionEnabled;
};

export const selectIsBuildDistroEnabledForOrg: (state: RootState) => boolean = (state) => {
    if (_uiConfig(state).demoData?.buildDistroDemoData) {
        return true;
    }

    if (!state.entities.apps.allIds?.length) {
        return false;
    }

    for (const id of state.entities.apps.allIds) {
        if (selectIsBuildDistroEnabled(state, id)) {
            return true;
        }
    }

    return false;
};

export const selectAutomationsByType: (
    state: RootState,
    appId: string,
    automationType: AutomationType,
) => Automation[] | undefined = (state, appId, automationType) => {
    const automations = selectAppSettingAutomations(state, appId);

    return automations?.filter((automation) => automation.type === automationType);
};

export const selectAutomationById: (
    state: RootState,
    appId: string,
    automationId: string | undefined,
) => Automation | undefined = (state, appId, automationId) => {
    if (automationId == null) {
        return undefined;
    }
    const automations = selectAppSettingAutomations(state, appId);

    return automations?.find((automation) => automation.id === automationId);
};

/** Safe boolean for "is this automation enabled?". Prefer over selectAutomationById(...).enabled to avoid undefined access. */
export const selectIsAutomationEnabled: (
    state: RootState,
    appId: string,
    automationId: string | undefined | null,
) => boolean = (state, appId, automationId) =>
    selectAutomationById(state, appId, automationId ?? undefined)?.enabled ?? false;

export const selectIsAutomationEnabledForReleaseType: (
    state: RootState,
    appId: string,
    automationId: string | undefined | null,
    releaseType: ReleaseType,
) => boolean = (state, appId, automationId, releaseType) => {
    const automation = selectAutomationById(state, appId, automationId ?? undefined);
    if (!automation) {
        return false;
    }

    const configPerReleaseType = automation.configPerReleaseType ?? {};
    return configPerReleaseType[getReleaseTypeForNonBlockingSteps(releaseType)]?.enabled ?? automation.enabled;
};

export const selectAutomationsById: (state: RootState, appId: string) => Record<string, Automation> = (
    state,
    appId,
) => {
    const automations = selectAppSettingAutomations(state, appId);

    return keyBy(automations, (automation) => automation.id);
};

// Returns the first auto config of a given type in the app
// Warning that there could be multiple automations of the same type in the app
// If you want to retrieve a specific automation, use selectAutomationById
export const selectFirstAutomationOfType: (
    state: RootState,
    appId: string,
    type: AutomationType,
) => Automation | undefined = (state, appId, type) => {
    const automations = selectAppSettingAutomations(state, appId);

    return automations.find((automation) => automation.type === type);
};

export const selectDefaultReviewAttachmentFromAutomation: (
    state: RootState,
    appId: string,
    automationId: string,
) => FileStats | null | undefined = (state, appId, automationId) => {
    const automationApplyReviewAttachment = selectAutomationById(
        state,
        appId,
        automationId,
    ) as AutomationApplyReviewAttachment;

    return automationApplyReviewAttachment?.data?.reviewAttachmentFile;
};

export const selectScheduleSettings: (state: RootState, appId: string) => ReleaseScheduleCadenceSettings = (
    state,
    appId,
) => {
    const app = selectApp(state, appId);

    return app.appSettings.releaseScheduleCadenceSettings;
};

export const selectLifecycleFreezes: (state: RootState, appId: string) => LifecycleFreezeSet[] = (state, appId) => {
    const app = selectApp(state, appId);

    return app.appSettings.lifecycleFreezes || [];
};

export const selectAppPlatform: (state: RootState, appId: string) => AppPlatform = (state, appId) => {
    const appSettings = selectAppSettings(state, appId);

    return appSettings?.platform;
};

export const selectShouldEnableExperimentalAIReleaseNotesFeature: (state: RootState, orgId: string) => boolean = (
    state,
    orgId,
) => {
    const org = selectOrg(state, orgId)!;

    return org.isEnabledAIReleaseNotesFeature;
};

export const selectAppStoreType: (state: RootState, appId: string) => AppStoreType | undefined = (state, appId) => {
    const appSettings = selectAppSettings(state, appId);
    return appSettings?.appStoreType;
};
export const selectUserAppDataByAppIdMap: (state: RootState, orgId: string) => Record<string, UserAppData> = (
    state,
    orgId,
) => {
    const userAppData: Record<string, UserAppData> = {};
    const allApps = getAllAppsInOrg(orgId, state);

    allApps.forEach((app) => {
        userAppData[app.appId] = {
            appId: app.appId,
            appName: app.appSettings.appName,
            platform: app.appSettings.platform,
            appStoreType: app.appSettings.appStoreType,
        };
    });

    return userAppData;
};

export const selectAppName: (state: RootState, appId: string) => string = (state, appId) => {
    const appSettings = selectAppSettings(state, appId);

    return appSettings?.appName;
};

export const selectAppSettings: (state: RootState, appId: string) => AppSettings = (state, appId) => {
    const app = selectApp(state, appId);

    return app?.appSettings;
};

export const selectAppTimeFormatSetting: (state: RootState, appId: string) => boolean = (state, appId) => {
    const appSettings = selectAppSettings(state, appId);
    return appSettings?.use24HourTimeFormat ?? false;
};

export const selectDestinationInfoByDestinationStepConfigId: (
    state: RootState,
    appId: string,
) => Record<string, Destination> = (state, appId) => {
    const appSettings = selectAppSettings(state, appId);

    return appSettings?.destinationInfoByDestinationStepConfigId ?? {};
};

export const selectWorkflowOverview: (state: RootState, appId: string) => WorkflowOverview | undefined = (
    state,
    appId,
) => {
    const app = selectApp(state, appId);
    return app?.workflowOverview;
};

export const selectWorkflowId = (state: RootState, appId: string): Nullable<string> => {
    const app = selectApp(state, appId);
    return app?.workflowId;
};

export const selectWorkflowRunId = (state: RootState, releaseId: string): Nullable<string> => {
    const release = selectRelease(state, releaseId);
    return release.workflowRunId;
};

export const selectIsWorkflowsMigratedAndEnabled: (state: RootState, appId: string) => boolean = (state, appId) => {
    const appSettings = selectAppSettings(state, appId);

    return appSettings?.isWorkflowsMigratedAndEnabled ?? false;
};

export const selectWorkflowEnabledSteps: (state: RootState, appId: string) => WorkflowEnabledSteps = (state, appId) => {
    const appSettings = selectAppSettings(state, appId);
    const isWorkflowsMigratedAndEnabled = selectIsWorkflowsMigratedAndEnabled(state, appId);
    return (isWorkflowsMigratedAndEnabled ? appSettings?.workflowEnabledSteps : undefined) ?? {};
};

export const selectIsWorkflowsRolloutStepEnabled: (state: RootState, appId: string) => boolean | undefined = (
    state,
    appId,
) => {
    const enabledSteps = selectWorkflowEnabledSteps(state, appId);

    return enabledSteps[ReleaseStepType.Rollout];
};

export const selectIsWorkflowsCIStepEnabled: (state: RootState, appId: string) => boolean = (state, appId) => {
    const enabledSteps = selectWorkflowEnabledSteps(state, appId);

    return !!enabledSteps[ReleaseStepType.ReleaseCandidate];
};

export const selectIsWorkflowsSSRStepsEnabled: (state: RootState, appId: string) => boolean | undefined = (
    state,
    appId,
) => {
    const enabledSteps = selectWorkflowEnabledSteps(state, appId);
    return (
        enabledSteps[ReleaseStepType.Submission] &&
        enabledSteps[ReleaseStepType.StoreReview] &&
        enabledSteps[ReleaseStepType.Takeoff]
    );
};

export const selectBranchConfigsById: (state: RootState, appId: string) => Record<string, BranchConfig> = (
    state,
    appId,
) => {
    const app = selectAppSettings(state, appId);
    return app?.branchConfigsById ?? {};
};

export const selectTagConfigsById: (state: RootState, appId: string) => Record<string, TagConfig> = (state, appId) => {
    const app = selectAppSettings(state, appId);
    return app?.tagConfigsById ?? {};
};

export const selectFeatureAffiliationConfigsById: (
    state: RootState,
    appId: string,
) => Record<string, FeatureAffiliationConfig> = (state, appId) => {
    const app = selectAppSettings(state, appId);
    return app?.featureAffiliationConfigsById ?? {};
};

export const selectSigningKeys: (state: RootState, appId: string) => SigningKey[] = (state, appId) => {
    const appSettings = selectAppSettings(state, appId);

    return appSettings?.signingKeys;
};

export const selectHasCompletedQualitativeOnboardingAtLeastOnce: (state: RootState, appId: string) => boolean = (
    state,
    appId,
) => {
    const appSettings = selectAppSettings(state, appId);
    return appSettings?.hasCompletedQualitativeOnboardingAtLeastOnce;
};

export const selectDefaultReleaseNotes: (
    state: RootState,
    appId: string,
) => AppStoreDefaultReleaseNotes[] | undefined = (state, appId) => {
    const appSettings = selectAppSettings(state, appId);

    return appSettings.appStoreDefaultReleaseNotes;
};

export const selectDefaultReleaseType: (state: RootState, appId: string) => ReleaseType = (state, appId) => {
    const appSettings = selectAppSettings(state, appId);

    return appSettings?.defaultReleaseType;
};

export const selectReleaseStepsBlockingConfigForReleaseType: (
    state: RootState,
    appId: string,
) => Record<ReleaseTypesForNonBlockingSteps, Record<ReleaseStepType, ReleaseStepBlockingConfig>> = (state, appId) => {
    const appSettings = selectAppSettings(state, appId);

    if (appSettings?.releaseStepsBlockingConfigForReleaseType == null) {
        return {
            standard: {
                kickoff: ReleaseStepBlockingConfig.Blocking,
                featureReadiness: ReleaseStepBlockingConfig.Blocking,
                releaseCandidate: ReleaseStepBlockingConfig.Blocking,
                regressionTesting: ReleaseStepBlockingConfig.Blocking,
                betaTesting: ReleaseStepBlockingConfig.Blocking,
                metadata: ReleaseStepBlockingConfig.Blocking,
                screenshots: ReleaseStepBlockingConfig.Blocking,
                approvals: ReleaseStepBlockingConfig.Blocking,
                submission: ReleaseStepBlockingConfig.Blocking,
                storeReview: ReleaseStepBlockingConfig.Blocking,
                takeoff: ReleaseStepBlockingConfig.Blocking,
                ciDistribution: ReleaseStepBlockingConfig.Blocking,
                vcsDistribution: ReleaseStepBlockingConfig.Blocking,
                rollbackBuild: ReleaseStepBlockingConfig.Blocking,
                translations: ReleaseStepBlockingConfig.Blocking,
                rollout: ReleaseStepBlockingConfig.Blocking,
                releaseCycle: ReleaseStepBlockingConfig.Blocking,
            },
            hotfix: {
                kickoff: ReleaseStepBlockingConfig.Blocking,
                featureReadiness: ReleaseStepBlockingConfig.Blocking,
                releaseCandidate: ReleaseStepBlockingConfig.Blocking,
                regressionTesting: ReleaseStepBlockingConfig.Blocking,
                betaTesting: ReleaseStepBlockingConfig.Blocking,
                metadata: ReleaseStepBlockingConfig.Blocking,
                screenshots: ReleaseStepBlockingConfig.Blocking,
                approvals: ReleaseStepBlockingConfig.Blocking,
                submission: ReleaseStepBlockingConfig.Blocking,
                storeReview: ReleaseStepBlockingConfig.Blocking,
                takeoff: ReleaseStepBlockingConfig.Blocking,
                ciDistribution: ReleaseStepBlockingConfig.Blocking,
                vcsDistribution: ReleaseStepBlockingConfig.Blocking,
                rollbackBuild: ReleaseStepBlockingConfig.Blocking,
                translations: ReleaseStepBlockingConfig.Blocking,
                rollout: ReleaseStepBlockingConfig.Blocking,
                releaseCycle: ReleaseStepBlockingConfig.Blocking,
            },
            rollback: {
                kickoff: ReleaseStepBlockingConfig.Blocking,
                featureReadiness: ReleaseStepBlockingConfig.Blocking,
                releaseCandidate: ReleaseStepBlockingConfig.Blocking,
                regressionTesting: ReleaseStepBlockingConfig.Blocking,
                betaTesting: ReleaseStepBlockingConfig.Blocking,
                metadata: ReleaseStepBlockingConfig.Blocking,
                screenshots: ReleaseStepBlockingConfig.Blocking,
                approvals: ReleaseStepBlockingConfig.Blocking,
                submission: ReleaseStepBlockingConfig.Blocking,
                storeReview: ReleaseStepBlockingConfig.Blocking,
                takeoff: ReleaseStepBlockingConfig.Blocking,
                ciDistribution: ReleaseStepBlockingConfig.Blocking,
                vcsDistribution: ReleaseStepBlockingConfig.Blocking,
                rollbackBuild: ReleaseStepBlockingConfig.Blocking,
                translations: ReleaseStepBlockingConfig.Blocking,
                rollout: ReleaseStepBlockingConfig.Blocking,
                releaseCycle: ReleaseStepBlockingConfig.Blocking,
            },
        };
    }

    return appSettings?.releaseStepsBlockingConfigForReleaseType;
};

export const selectShouldHideNonBlockingSteps: (state: RootState, appId: string) => boolean = (state, appId) => {
    const appSettings = selectAppSettings(state, appId);

    return appSettings?.shouldHideNonBlockingSteps ?? false;
};

export const selectRequiredSteps: (state: RootState, appId: string) => ReleaseStepType[] = (state, appId) => {
    const appSettings = selectAppSettings(state, appId);

    return appSettings?.requiredReleaseStepTypesForApp;
};

export const selectNotifications: (state: RootState, appId: string) => AppNotification[] = (state, appId) => {
    const settings = selectAppSettings(state, appId);

    return settings?.notifications ?? [];
};

export const selectCommonNotificationFromAppNotificationId: (
    state: RootState,
    appId: string,
    notificationId: AppNotificationType,
) => CommonNotification | undefined = (state, appId, notificationId) => {
    const notifications = selectNotifications(state, appId);
    const notification = notifications.find((notif) => notif.id === notificationId);
    return notification;
};

export const selectCommonNotificationFromBuildDistroNotificationId: (
    state: RootState,
    appId: string,
    bucketId: string,
    notificationId: BuildDistroNotificationType,
) => CommonNotification | undefined = (state, appId, bucketId, notificationId) => {
    const bucket = selectBuildDistributionBucketById(state, bucketId);
    const notification = bucket?.notifications?.find((notif) => notif.id === notificationId);
    if (!notification) {
        return undefined;
    }
    return {
        id: notification.id,
        displayText: notification.displayText,
        info: notification.info,
        enabled: notification.enabled,
        channels: notification.channels,
        mentionGroups: notification.mentionGroups,
        mentionUsers: notification.mentionUsers,
        hourOfDayForNotification: notification.hourOfDayForNotification,
        emailsToInclude: notification.emailsToInclude,
        allowEmails: notification.allowEmails,
        notificationChannels: notification.notificationChannels,
    };
};

export const selectAppIntegrations: (state: RootState, appId: string) => Integration[] = (state, appId) => {
    const appSettings = selectAppSettings(state, appId);

    return appSettings?.integrations ?? [];
};

export const selectAppSettingAutomations: (state: RootState, appId: string) => Automation[] = (state, appId) => {
    const appSettings = selectAppSettings(state, appId);

    return appSettings?.automations;
};

export const selectAppSettingChecklistItemData: (state: RootState, appId: string) => ChecklistItemTemplate[] = (
    state,
    appId,
) => {
    const appSettings = selectAppSettings(state, appId);

    return appSettings?.stepChecklistItems;
};

export const selectReleasesForAppId: (state: RootState, appId: string) => Release[] = (state, appId) => {
    return getAllReleasesForAppId(appId, state);
};

export const selectReleaseStatus: (state: RootState, releaseId: string) => ReleaseStatus = (state, releaseId) => {
    const release = selectRelease(state, releaseId);

    return release.status;
};

export const selectReleaseOverviewStats: (state: RootState, releaseId: string) => ReleaseOverviewStats = (
    state,
    releaseId,
) => {
    const release = selectRelease(state, releaseId);

    return release.overview;
};

export const selectReleaseStatsHistory: (state: RootState, releaseId: string) => ReleaseStatistics[] = (
    state,
    releaseId,
) => {
    const release = selectRelease(state, releaseId);
    return release?.releaseStatsHistory ?? [];
};

export const selectReleaseOverview: (state: RootState, releaseId: string) => ReleaseOverview | undefined = (
    state,
    releaseId,
) => {
    const release = selectRelease(state, releaseId);
    return release?.releaseOverview;
};

export const selectRolloutMonitoring: (state: RootState, releaseId: string) => RolloutMonitoring | undefined | null = (
    state,
    releaseId,
) => {
    const release = selectRelease(state, releaseId);
    return release?.rolloutMonitoring;
};

export const selectAppStoreDefaults: (state: RootState, appId: string) => AppStoreDefaultSettings | undefined = (
    state,
    appId,
) => {
    const app = selectApp(state, appId);

    return app?.appSettings.appStoreDefaults;
};

export const selectKickedOffAt: (state: RootState, releaseId: string) => string | undefined = (state, releaseId) => {
    const release = selectRelease(state, releaseId);

    return release.kickedOffAt;
};

export const selectSubmittedAt: (state: RootState, releaseId: string) => string | undefined = (state, releaseId) => {
    const release = selectRelease(state, releaseId);

    return release.submittedAt;
};

export const selectReleasedAt: (state: RootState, releaseId: string) => string | undefined = (state, releaseId) => {
    const release = selectRelease(state, releaseId);

    return release.releasedAt;
};

export const selectIsReleasePaused: (state: RootState, releaseId: string) => boolean = (state, releaseId) => {
    const release = selectRelease(state, releaseId);

    return release.scheduledAutomationsPaused;
};

export const selectReleaseStep: (
    state: RootState,
    releaseId: string,
    releaseStepType: ReleaseStepType,
    stepRunId?: string,
) => ReleaseStep = (state, releaseId, releaseStepType, stepRunId) => {
    const releaseStepId = stepRunId ?? generateBackwardsCompatibleReleaseStepRunId(releaseId, releaseStepType);
    if (_uiConfig(state)?.demoData?.releaseStepsDemoData?.[releaseStepId]) {
        return _uiConfig(state).demoData.releaseStepsDemoData[releaseStepId].releaseStep;
    }

    const { entities } = state;
    return entities.releaseSteps.byId[releaseStepId];
};

export const selectAllReleaseSteps: (state: RootState) => ReleaseStep[] = (state) => {
    if (_uiConfig(state)?.demoData?.releaseStepsDemoData?.releaseStepsDemoData) {
        return Object.values(_uiConfig(state).demoData.releaseStepsDemoData);
    }

    const { entities } = state;
    return Object.values(entities.releaseSteps.byId);
};

export const selectStepConfigsByType: (
    state: RootState,
    appId: string,
    type: StepArchetype,
) => Record<string, StepGraphStep> = (state, appId, type) => {
    const stepConfigsMap = selectStepConfigDataFromStepGraphs(state, appId);
    return Object.entries(stepConfigsMap)
        .filter(([_, value]) => value.archetype === type)
        .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});
};

export const selectStepRun: (state: RootState, stepRunId: string) => StepRun = (state, stepRunId) => {
    if (_uiConfig(state)?.demoData?.stepRunsDemoData?.[stepRunId]) {
        return cloneDeep(_uiConfig(state).demoData.stepRunsDemoData[stepRunId]);
    }

    const { entities } = state;
    return entities.stepRuns.byId[stepRunId];
};

export const selectStepRunOrReleaseStep: (
    state: RootState,
    releaseId: string,
    releaseStepType: ReleaseStepType,
    stepRunId?: string,
) => ReleaseStep | StepRun = (state, releaseId, releaseStepType, stepRunId) => {
    if (stepRunId) {
        return selectStepRun(state, stepRunId);
    } else {
        return selectReleaseStep(state, releaseId, releaseStepType);
    }
};

export const selectStepArchetypeGroupByGroupIndexAndArchetypeGroupIndex: (
    state: RootState,
    workflowRunId: string,
    groupIndex: number,
    archetypeGroupIndex: number,
) => Nullable<StepArchetypeGroup> = (state, workflowRunId, groupIndex, archetypeGroupIndex) => {
    const stepRunGraph = selectStepRunGraph(state, workflowRunId);
    const stepGroup = stepRunGraph.stepGroups?.[groupIndex];
    const archetypeGroup = stepGroup?.stepArchetypeGroups?.[archetypeGroupIndex];
    return archetypeGroup ?? null;
};

export const selectStepRunsByGroupIndexAndArchetypeGroupIndex: (
    state: RootState,
    workflowRunId: string,
    groupIndex: number,
    archetypeGroupIndex: number,
) => Nullable<StepRunStatusData[]> = (state, workflowRunId, groupIndex, archetypeGroupIndex) => {
    const archetypeGroup = selectStepArchetypeGroupByGroupIndexAndArchetypeGroupIndex(
        state,
        workflowRunId,
        groupIndex,
        archetypeGroupIndex,
    );
    return archetypeGroup?.stepRunStatusDatum ?? null;
};

export const selectStepConfig: (state: RootState, stepConfigId: string) => StepConfig = (state, stepConfigId) => {
    if (_uiConfig(state)?.demoData?.stepConfigsDemoData) {
        return _uiConfig(state).demoData.stepConfigsDemoData[stepConfigId];
    }

    const { entities } = state;
    return entities.stepConfigs.byId[stepConfigId];
};

export const selectAppleAppStoreData: (state: RootState, releaseId: string) => AppleAppStoreData = (
    state,
    releaseId,
) => {
    const releaseStep = selectReleaseStep(state, releaseId, ReleaseStepType.Takeoff) as ReleaseStepTakeoff;
    const data = releaseStep?.data as ReleaseStepTakeoffData;
    const appStoreData = data?.appStoreData;
    return appStoreData as AppleAppStoreData;
};

export const selectAutoKickoffStepAutomationRun: (state: RootState, releaseId: string) => AutomationRun | undefined = (
    state,
    releaseId,
) => {
    const kickoffStep = selectReleaseStep(state, releaseId, ReleaseStepType.Kickoff) as ReleaseStepKickoff;

    return kickoffStep?.automationRuns?.find((automation) => automation.type === AutomationType.AutoKickoffRelease);
};

export const selectAutoSubmitStepAutomationRun: (state: RootState, releaseId: string) => AutomationRun | undefined = (
    state,
    releaseId,
) => {
    const submissionStep = selectReleaseStep(state, releaseId, ReleaseStepType.Submission) as ReleaseStepSubmission;

    return submissionStep?.automationRuns?.find((automation) => automation.type === AutomationType.AutoSubmitRelease);
};

export const selectAutoReleaseStepAutomationRun: (state: RootState, releaseId: string) => AutomationRun | undefined = (
    state,
    releaseId,
) => {
    const releaseStep = selectReleaseStep(state, releaseId, ReleaseStepType.Takeoff) as ReleaseStepTakeoff;

    return releaseStep?.automationRuns?.find((automation) => automation.type === AutomationType.AutoRelease);
};

export const selectDisallowedUserActions: (state: RootState) => RunwayUserAction[] | undefined = (state) => {
    return state.user.disallowedActions;
};

export const selectIsPilotForRelease: (state: RootState, releaseId: string) => boolean = (state, releaseId) => {
    const release = selectRelease(state, releaseId);
    const releasePilotId = release?.releasePilotId;

    return releasePilotId === state.user.id;
};

export const selectActivePilotForRelease: (
    state: RootState,
    orgId: string,
    releaseId: string,
) => OrgUser | undefined = (state, orgId, releaseId) => {
    const release = selectRelease(state, releaseId);
    const releasePilotId = release?.releasePilotId;
    return releasePilotId ? selectOrgUser(state, orgId, releasePilotId) : undefined;
};

export const selectReleaseIsRunwayAutomated: (state: RootState, appId: string, releaseId: string) => boolean = (
    state,
    appId,
    releaseId,
) => {
    const appStoreType = selectAppStoreType(state, appId);

    if (appStoreType && getIsAppleAppStore(appStoreType)) {
        const releaseAutomation = selectAutoReleaseStepAutomationRun(state, releaseId);

        if (!releaseAutomation) {
            return false;
        }

        return automationIsEnabledAndActiveAndScheduled(releaseAutomation);
    }

    return false;
};

export const selectReleaseSuccessfullyRunwayAutomated: (
    state: RootState,
    appId: string,
    releaseId: string,
) => boolean = (state, appId, releaseId) => {
    const appStoreType = selectAppStoreType(state, appId);

    if (appStoreType && getIsAppleAppStore(appStoreType)) {
        const releaseAutomation = selectAutoReleaseStepAutomationRun(state, releaseId);

        if (!releaseAutomation) {
            return false;
        }

        return automationIsCompleted(releaseAutomation);
    }

    return false;
};

export const selectSubmissionIsRunwayAutomated: (state: RootState, appId: string, releaseId: string) => boolean = (
    state,
    appId,
    releaseId,
) => {
    const appPlatform = selectAppPlatform(state, appId);

    if (isAppPlatform(appPlatform)) {
        const submitAutomation = selectAutoSubmitStepAutomationRun(state, releaseId);

        if (!submitAutomation) {
            return false;
        }

        return automationIsEnabledAndActiveAndScheduled(submitAutomation);
    }

    return false;
};

export const selectSubmissionSuccessfullyRunwayAutomated: (
    state: RootState,
    appId: string,
    releaseId: string,
) => boolean = (state, appId, releaseId) => {
    const appPlatform = selectAppPlatform(state, appId);

    if (isAppPlatform(appPlatform)) {
        const submitAutomation = selectAutoSubmitStepAutomationRun(state, releaseId);

        if (!submitAutomation) {
            return false;
        }

        return automationIsCompleted(submitAutomation);
    }

    return false;
};

export const selectIsSubmitAutomationInactiveMissingAppStoreDefaults: (
    state: RootState,
    appId: string,
    releaseId: string,
) => boolean | undefined = (state, appId, releaseId) => {
    const submitAutomation = selectAutoSubmitStepAutomationRun(state, releaseId);
    const appStoreDefaults = selectAppStoreDefaults(state, appId);
    const releaseType = selectReleaseType(state, releaseId);

    return (
        submitAutomation &&
        automationIsBlocked(submitAutomation) &&
        !appStoreDefaults &&
        releaseType !== ReleaseType.Hotfix
    );
};

export const selectIsRunwayOrg = (state: RootState) => {
    const org = selectOrg(state)!;
    return TEST_ORG_IDS.includes(org.orgId);
};

export const selectBucketDisallowedActions: (state: RootState, bucketId: string) => RunwayUserAction[] = (
    state,
    bucketId,
) => {
    const bucket = selectBuildDistributionBucketById(state, bucketId);

    return bucket?.disallowedActions ?? [];
};

// Pass in the releaseId if this is a release-specific action

export const selectUserCanPerformAction: (
    state: RootState,
    actionType: RunwayUserAction,
    releaseId?: string,
    environment?: Environment,
    bucketId?: string,
    allowDemoMode?: boolean,
) => boolean | undefined = (
    state: RootState,
    actionType: RunwayUserAction,
    releaseId?: string,
    environment: Environment = process.env.NODE_ENV as Environment,
    bucketId?: string,
    allowDemoMode?: boolean,
) => {
    const orgId = selectOrg(state)!.orgId;
    const isReleasePilot = releaseId ? selectIsPilotForRelease(state, releaseId) : false;
    const isDisallowedAction = state.user.disallowedActions?.includes(actionType);
    const isRunwayAdmin = userIsRunwayAdmin(state.user);
    const isRunwayAdminReadonly = userIsRunwayAdminReadOnly(state.user);
    const runwayAdminLiveModeEnabled = _uiConfig(state).runwayAdminLiveModeEnabled;
    const isReleaseDemoModeEnabled = releaseId ? selectIsDemoModeEnabledForRelease(state, releaseId) : false;
    const isAppsDemoModeEnabled = selectIsAppsDemoModeEnabled(state);
    const disallowedBucketActions = bucketId ? selectBucketDisallowedActions(state, bucketId) : [];
    const isBucketDisallowedAction = actionType && disallowedBucketActions.includes(actionType);

    if (isReleaseDemoModeEnabled || isAppsDemoModeEnabled) {
        // everything should be disabled under demo mode if allowDemoMode is false
        return allowDemoMode;
    } else if (isReleasePilot) {
        // TODO! take into account allowed list of pilot actions
        return true;
    } else if (isRunwayAdmin || isRunwayAdminReadonly) {
        const isDevEnvironment = environment === Environment.Development;

        // disable live mode if either in dev env, or on a current runway test org
        if (isDevEnvironment || TEST_ORG_IDS.includes(orgId)) {
            return true;
        }

        return runwayAdminLiveModeEnabled;
    } else {
        return !isDisallowedAction && !isBucketDisallowedAction;
    }
};

export const selectOrgHasAnyApps: (state: RootState) => boolean = (state) => {
    const allAppIds = state.entities.apps.allIds;

    return allAppIds.length > 0;
};

export const selectUserHasViewedOnboarding: (state: RootState) => boolean = (state) => {
    const userViewedOnboarding: StringKeyedDictionary<boolean> = Storage.getItem(Storage.USER_HAS_VIEWED_ONBOARDING);

    return userViewedOnboarding ? userViewedOnboarding[state.user.id] : false;
};

export const selectShowOnboarding: (state: RootState, orgId: string) => boolean = (state, orgId) => {
    const orgHasAnyApps = selectOrgHasAnyApps(state);
    const userHasViewedOnboarding = selectUserHasViewedOnboarding(state);
    const needsNewPassword = state.user.needsNewPassword;
    const isDemoInstance = selectIsDemoInstance(state, orgId);

    return !orgHasAnyApps && !userHasViewedOnboarding && !needsNewPassword && !isDemoInstance;
};

export const selectUser: (state: RootState) => User = (state) => {
    return state.user;
};

// selects a user from the org
export const selectOrgUser: (state: RootState, orgId: string, userId: string) => OrgUser | undefined = (
    state,
    orgId,
    userId,
) => {
    if (_uiConfig(state).demoData?.orgUsersDemoData) {
        return _uiConfig(state).demoData.orgUsersDemoData[userId];
    }

    const org = selectOrg(state, orgId)!;
    return org.users.find((user) => user.id === userId);
};

export const selectAppUser: (state: RootState, appId: string, userId: string) => OrgUser | undefined = (
    state,
    appId,
    userId,
) => {
    const users = selectAppUsers(state, appId);

    return users.find((user) => user.id === userId);
};

// special helpers that grab from app users if available, otherwise try org users

export const selectUserById: (state: RootState, orgId: string, appId: string, userId: string) => OrgUser | undefined = (
    state,
    orgId,
    appId,
    userId,
) => {
    const appUsers = selectAppUsers(state, appId);

    const appUser = appUsers.find((user) => user.id === userId);

    if (appUser) {
        return appUser;
    }

    const orgUsers = selectOrgUsers(state, orgId);

    return orgUsers.find((user) => user.id === userId);
};

export const selectCLIUsers: (state: RootState, orgId: string, appId: string, userIds: string[]) => OrgUser[] = (
    state,
    orgId,
    appId,
    userIds,
) => {
    if (!userIds) {
        return [];
    }

    const users = userIds.map((userId) => selectUserById(state, orgId, appId, userId));

    return compact(users);
};

export const selectUserIsAdmin: (state: RootState) => boolean = (state) => {
    return state.user.permissions.includes(UserPermissions.Admin);
};

export const selectUserIsRunwayAdmin: (state: RootState) => boolean = (state) => {
    return (
        state?.user?.permissions?.some((permission) => {
            const runwayAdminPermissions = [UserPermissions.RunwayAdmin, UserPermissions.RunwayAdminReadOnly];

            return runwayAdminPermissions.includes(permission);
        }) ?? false
    );
};

export const selectStepRunHasFailedAutomation: (state: RootState, appId: string, stepRunId: string) => boolean = (
    state,
    appId,
    stepRunId,
) => {
    return selectReleaseStepHasFailedAutomation(state, appId, '', null as unknown as ReleaseStepType, stepRunId);
};

export const selectReleaseStepHasFailedAutomation: (
    state: RootState,
    appId: string,
    releaseId: string,
    releaseStepType: ReleaseStepType,
    stepRunId?: string,
) => boolean = (state, appId, releaseId, releaseStepType, stepRunId) => {
    const automationsById = selectAutomationsById(state, appId);
    let automationRuns: AutomationRun[] = [];
    // If the automation is rollout, need to use a separate selector
    if (releaseStepType != ReleaseStepType.Rollout) {
        const stepRunOrReleaseStep = selectStepRunOrReleaseStep(state, releaseId, releaseStepType, stepRunId);
        automationRuns = stepRunOrReleaseStep.automationRuns ?? [];
    } else {
        automationRuns = selectRolloutMonitoring(state, releaseId)?.automationRuns ?? [];
    }
    return !!automationRuns.find((automation) => {
        const automationConfig = automationsById[automation.id];

        if (!automationConfig) {
            Logger.warn('automationConfig for automationRun not found by id');
        }

        return (
            automationConfig ?
                automationConfig.enabled ?
                    automationIsFailed(automation)
                :   false
            :   false
        );
    });
};

export const selectStepRunHasBlockedAutomation: (state: RootState, appId: string, stepRunId: string) => boolean = (
    state,
    appId,
    stepRunId,
) => {
    return selectReleaseStepHasBlockedAutomation(state, appId, '', null as unknown as ReleaseStepType, stepRunId);
};

export const selectReleaseStepHasBlockedAutomation: (
    state: RootState,
    appId: string,
    releaseId: string,
    releaseStepType: ReleaseStepType,
    stepRunId?: string,
) => boolean = (state, appId, releaseId, releaseStepType, stepRunId) => {
    const automationsById = selectAutomationsById(state, appId);

    let automationRuns: AutomationRun[] = [];
    // If the automation is rollout, need to use a separate selector
    if (releaseStepType != ReleaseStepType.Rollout) {
        const stepRunOrReleaseStep = selectStepRunOrReleaseStep(state, releaseId, releaseStepType, stepRunId);
        automationRuns = stepRunOrReleaseStep.automationRuns ?? [];
    } else {
        automationRuns = selectRolloutMonitoring(state, releaseId)?.automationRuns ?? [];
    }

    return !!automationRuns.find((automation) => {
        const automationConfig = automationsById[automation.id];

        if (!automationConfig) {
            Logger.warn('automationConfig for automationRun not found by id');
        }
        // automationConfig should never be undefined here, but protecting against for now jic
        return (
            automationConfig ?
                automationConfig.enabled ?
                    automationIsBlocked(automation)
                :   false
            :   false
        );
    });
};

export const selectMergeAccountsEnabled: (state: RootState, orgId: string) => boolean = (state, orgId) => {
    const org = selectOrg(state, orgId)!;

    return org.mergeAccountsWithAllowedDomainEnabled;
};

export const selectAddNewUsersToAppsSettingEnabled: (state: RootState, orgId: string) => boolean = (state, orgId) => {
    const org = selectOrg(state, orgId)!;
    return org.addNewUsersToAppsSettingEnabled;
};

export const selectAIDisabledForAppStoreReviews: (state: RootState, orgId: string) => boolean = (state, orgId) => {
    const org = selectOrg(state, orgId)!;

    return org.disableAIForAppStoreReviews;
};

export const selectAIDisabledForReleaseNotes: (state: RootState, orgId: string) => boolean = (state, orgId) => {
    const org = selectOrg(state, orgId)!;

    return org.disableAIForReleaseNotes;
};

export const selectAIDisabledForOrgInsights: (state: RootState, orgId: string) => boolean = (state, orgId) => {
    const org = selectOrg(state, orgId)!;
    return org.disableAIForOrgInsights || false;
};

export const selectUserIsLoggedIn: (state: RootState) => boolean = (state) => {
    return state.auth.isLoggedIn;
};

export const selectIsStateFetched: (state: RootState) => boolean = (state) => {
    return state.user !== null;
};

export const selectWsIsConnected: (state: RootState) => boolean = (state) => {
    return state.ui.wsState.isConnected;
};

export const selectHasOpenedWsConnection: (state: RootState) => boolean = (state) => {
    return state.ui.wsState.hasPreviouslyOpenedConnection;
};

export const selectIsAttemptingWsReconnect: (state: RootState) => boolean = (state) => {
    return state.ui.wsState.isAttemptingReconnect;
};

export const selectCanAttemptReconnect: (state: RootState) => boolean = (state) => {
    const isWsConnected = selectWsIsConnected(state);
    const isLoggedIn = selectUserIsLoggedIn(state);
    const isStateFetched = selectIsStateFetched(state);
    const hasOpenedConnection = selectHasOpenedWsConnection(state);
    const websocketsEnabledInConfig = selectRunwayAdminWebsocketsEnabled(state);

    return hasOpenedConnection && !isWsConnected && isLoggedIn && isStateFetched && websocketsEnabledInConfig;
};

export const selectAppHasRequiredCapabilitiesConfigured: (state: RootState, appId: string) => boolean = (
    state,
    appId,
) => {
    const appSettings = selectAppSettings(state, appId);
    const requiredCapabilitiesForApp = requiredCapabilitiesForPlatform(appSettings.platform);

    // Check if we have at least 1 integration for every capability
    return reduce(
        requiredCapabilitiesForApp,
        (hasIntegrationForCapability, capability) => {
            if (hasIntegrationForCapability) {
                const integration = appSettings.integrations.find((integration) =>
                    integration.capabilities.includes(capability),
                );

                return !!integration;
            } else {
                return false;
            }
        },
        true,
    );
};

export const selectCompletedCapabilitiesFromCapabilities: (
    state: RootState,
    appId: string,
    capabilities: IntegrationCapability[],
) => IntegrationCapability[] = (state, appId, capabilities) => {
    const completedCapabilities: IntegrationCapability[] = [];

    forEach(capabilities, (capability) => {
        const integrationForCapability = selectIntegrationForCapability(state, appId, capability);

        if (integrationForCapability) {
            completedCapabilities.push(capability);
        }
    });

    return completedCapabilities;
};

export const selectAppHasSelectCapabilitiesConfigured: (
    state: RootState,
    appId: string,
    capabilities: IntegrationCapability[],
) => boolean = (state, appId, capabilities) => {
    const appSettings = selectAppSettings(state, appId);

    return reduce(
        capabilities,
        (hasIntegrationForCapability, capability) => {
            if (hasIntegrationForCapability) {
                const integration = appSettings.integrations.find((integration) =>
                    integration.capabilities.includes(capability),
                );

                return !!integration;
            } else {
                return false;
            }
        },
        true,
    );
};

export const selectRecommendedNotConnectedCapabilitiesForStep: (
    state: RootState,
    appId: string,
    releaseStepType: ReleaseStepType,
) => IntegrationCapability[] = (state, appId, releaseStepType) => {
    const recommendedCaps = recommendedCapabilitiesForReleaseStepType(releaseStepType) ?? [];
    const integrations = selectAppIntegrations(state, appId);

    return recommendedCaps.filter((capability) => {
        const integrationWithCapability = integrations.find((integration) =>
            integration.capabilities.includes(capability),
        );

        return !integrationWithCapability;
    });
};

export const selectCanEnableMergeAccountsSetting: (state: RootState, orgId: string) => boolean = (state, orgId) => {
    const org = selectOrg(state, orgId)!;

    return org.allowedDomains.length > 0;
};

export const selectHasVCSIntegrationConnected: (state: RootState, appId: string) => boolean = (state, appId) => {
    const vcsIntegration = selectVCSIntegration(state, appId);
    return !!vcsIntegration;
};

export const selectVCSIntegration: (state: RootState, appId: string) => Integration | undefined = (state, appId) => {
    const integrations = selectAppIntegrations(state, appId);
    const isRunwayOrg = selectIsRunwayOrg(state);
    const org = selectOrg(state)!;
    const allVCSIntegrationTypes = allSupportedIntegrationTypesForCapability(
        IntegrationCapability.VersionControl,
        isRunwayOrg,
        org.orgId,
    );

    return integrations.find((integration) => allVCSIntegrationTypes.includes(integration.id));
};

export const selectAllSupportedVCSIntegrationTypes: (state: RootState) => IntegrationType[] = (state) => {
    const isRunwayOrg = selectIsRunwayOrg(state);
    const org = selectOrg(state)!;
    return allSupportedIntegrationTypesForCapability(IntegrationCapability.VersionControl, isRunwayOrg, org.orgId);
};

export const selectFeatureManagementIntegration: (state: RootState, appId: string) => Integration | undefined = (
    state,
    appId,
) => {
    const integrations = selectAppIntegrations(state, appId);
    const isRunwayOrg = selectIsRunwayOrg(state);
    const org = selectOrg(state)!;
    const allFeatureManagementTypes = allSupportedIntegrationTypesForCapability(
        IntegrationCapability.FeatureManagement,
        isRunwayOrg,
        org.orgId,
    );
    return integrations.find((integration) => allFeatureManagementTypes.includes(integration.id));
};

export const selectIntegrationsForCapability: (
    state: RootState,
    appId: string,
    capability: IntegrationCapability,
) => Integration[] = (state, appId, capability) => {
    const integrations = selectAppIntegrations(state, appId);
    const isRunwayOrg = selectIsRunwayOrg(state);
    const org = selectOrg(state)!;
    const allIntegrationsForCapability = allSupportedIntegrationTypesForCapability(capability, isRunwayOrg, org.orgId);

    return integrations.filter((integration) => allIntegrationsForCapability.includes(integration.id));
};

// deprecated: use selectIntegrationsWithCapabilityFromInstallations instead
export const selectIntegrationWithCapabilityFromInstallations: (
    state: RootState,
    appId: string,
    capability: IntegrationCapability,
    integrationInstallationIds: IntegrationInstallationId[],
) => Integration | undefined = (state, appId, capability, integrationInstallationIds) => {
    const integrations = selectIntegrationsForCapability(state, appId, capability);
    return integrations.find((integration) => integrationInstallationIds.includes(integration.installationId));
};

export const selectIntegrationsWithCapabilityFromInstallations: (
    state: RootState,
    appId: string,
    capability: IntegrationCapability,
    integrationInstallationIds: IntegrationInstallationId[],
) => Integration[] = (state, appId, capability, integrationInstallationIds) => {
    const integrations = selectIntegrationsForCapability(state, appId, capability);
    return integrations.filter((integration) => integrationInstallationIds.includes(integration.installationId));
};

export const selectIntegrationForCapability: (
    state: RootState,
    appId: string,
    capability: IntegrationCapability,
) => Integration | undefined = (state, appId, capability) => {
    const integrations = selectIntegrationsForCapability(state, appId, capability);

    if (integrations.length > 0) {
        return integrations[0];
    }
    return undefined;
};

// services: Map<IntegrationInstallationId, AppDistributionService>;
export const selectAppDistributionServices: (
    state: RootState,
    appId: string,
) => Map<IntegrationInstallationId, AppDistributionService> = (state, appId) => {
    const appStores = selectIntegrationsForCapability(state, appId, IntegrationCapability.AppStore);
    const betas = selectIntegrationsForCapability(state, appId, IntegrationCapability.BetaTesting);

    const appDistributionServices = new Map<IntegrationInstallationId, AppDistributionService>();
    for (const appStore of appStores) {
        if (isAppDistributionService(appStore)) {
            appDistributionServices.set(appStore.installationId, appStore);
        }
    }

    for (const betaTesting of betas) {
        if (isAppDistributionService(betaTesting)) {
            appDistributionServices.set(betaTesting.installationId, betaTesting);
        }
    }

    return appDistributionServices;
};

function isAppDistributionService(obj: any): obj is AppDistributionService {
    return AppDistributionServiceIntegrationTypes.includes(obj.id);
}

export const selectBetaTestingIntegrations: (state: RootState, appId: string) => Integration[] = (state, appId) => {
    return selectIntegrationsForCapability(state, appId, IntegrationCapability.BetaTesting);
};

// Deprecated: use selectBetaTestingIntegrations instead
export const selectBetaTestingIntegration: (state: RootState, appId: string) => Integration | undefined = (
    state,
    appId,
) => {
    const integrations = selectAppIntegrations(state, appId);
    const isRunwayOrg = selectIsRunwayOrg(state);
    const org = selectOrg(state)!;
    const allBetaTestingTypes = allSupportedIntegrationTypesForCapability(
        IntegrationCapability.BetaTesting,
        isRunwayOrg,
        org.orgId,
    );

    return integrations.find((integration) => allBetaTestingTypes.includes(integration.id));
};

export const selectSDKDistributionIntegration: (state: RootState, appId: string) => Integration | undefined = (
    state,
    appId,
) => {
    const integrations = selectAppIntegrations(state, appId);
    const isRunwayOrg = selectIsRunwayOrg(state);
    const org = selectOrg(state)!;
    const allSDKDistributionTypes = allSupportedIntegrationTypesForCapability(
        IntegrationCapability.SDKDistribution,
        isRunwayOrg,
        org.orgId,
    );

    return integrations.find((integration) => allSDKDistributionTypes.includes(integration.id));
};

export const selectNotificationIntegration: (state: RootState, appId: string) => Integration | undefined = (
    state,
    appId,
) => {
    const integrations = selectAppIntegrations(state, appId);
    const isRunwayOrg = selectIsRunwayOrg(state);
    const org = selectOrg(state)!;
    const allNotificationTypes = allSupportedIntegrationTypesForCapability(
        IntegrationCapability.Notifications,
        isRunwayOrg,
        org.orgId,
    );

    return integrations.find((integration) => allNotificationTypes.includes(integration.id));
};

export const selectRegressionTestingIntegration: (state: RootState, appId: string) => Integration | undefined = (
    state,
    appId,
) => {
    const integrations = selectAppIntegrations(state, appId);
    const isRunwayOrg = selectIsRunwayOrg(state);
    const org = selectOrg(state)!;
    const allRegressionTestingTypes = allSupportedIntegrationTypesForCapability(
        IntegrationCapability.RegressionTesting,
        isRunwayOrg,
        org.orgId,
    );

    return integrations.find((integration) => allRegressionTestingTypes.includes(integration.id));
};

export const selectStabilityIntegrations: (state: RootState, appId: string) => Integration[] = (state, appId) => {
    return selectIntegrationsForCapability(state, appId, IntegrationCapability.StabilityMonitoring);
};

export const selectObservabilityAndAnalyticsIntegration: (
    state: RootState,
    appId: string,
) => Integration | undefined = (state, appId) => {
    const integrations = selectAppIntegrations(state, appId);
    const isRunwayOrg = selectIsRunwayOrg(state);
    const org = selectOrg(state)!;
    const allObservabilityAndAnalyticsTypes = allSupportedIntegrationTypesForCapability(
        IntegrationCapability.ObservabilityAndAnalytics,
        isRunwayOrg,
        org.orgId,
    );
    return integrations.find((integration) => allObservabilityAndAnalyticsTypes.includes(integration.id));
};

export const selectFeatureFlaggingIntegration: (state: RootState, appId: string) => Integration | undefined = (
    state,
    appId,
) => {
    const integrations = selectAppIntegrations(state, appId);
    const isRunwayOrg = selectIsRunwayOrg(state);
    const org = selectOrg(state)!;
    const allFeatureFlaggingTypes = allSupportedIntegrationTypesForCapability(
        IntegrationCapability.FeatureFlagging,
        isRunwayOrg,
        org.orgId,
    );

    return integrations.find((integration) => allFeatureFlaggingTypes.includes(integration.id));
};

export const selectTranslationsIntegration: (state: RootState, appId: string) => Integration | undefined = (
    state,
    appId,
) => {
    const integrations = selectAppIntegrations(state, appId);
    const isRunwayOrg = selectIsRunwayOrg(state);
    const org = selectOrg(state)!;

    const allTranslationsTypes = allSupportedIntegrationTypesForCapability(
        IntegrationCapability.Translations,
        isRunwayOrg,
        org.orgId,
    );

    return integrations.find((integration) => allTranslationsTypes.includes(integration.id));
};

export const selectUserflowIdentitySignature: (state: RootState) => string | undefined = (state) => {
    return state.auth.userflowIdentitySignature;
};

export const selectAuthRedirectUrl: (state: RootState) => string | undefined = (state) => {
    return state.auth.authRedirectUrl;
};

export const selectIsSSOSAMLLoginRequired: (state: RootState) => boolean = (state) => {
    return (state.appConfig as any)?.isSSOSamlRequired;
};

export const selectIsStatelessServer: (state: RootState) => boolean = (state) => {
    // defaults to true if config has not been fetched yet
    return (state.appConfig as any)?.serverType === ServerType.Stateless;
};

export const selectIncompleteCapabilitiesFromCapabilities: (
    state: RootState,
    appId: string,
    capabilities: IntegrationCapability[],
) => IntegrationCapability[] = (state, appId, capabilities) => {
    const incompleteCapabilities: IntegrationCapability[] = [];

    forEach(capabilities, (capability) => {
        const integrationForCapability = selectIntegrationForCapability(state, appId, capability);

        if (!integrationForCapability) {
            incompleteCapabilities.push(capability);
        }
    });

    return incompleteCapabilities;
};

export const selectIncompleteCapabilitiesForApp: (
    state: RootState,
    orgId: string,
    appId: string,
) => IntegrationCapability[] = (state, orgId, appId) => {
    const platform = selectAppPlatform(state, appId);
    const capabilities = integrationCapabilitiesForPlatform(platform, orgId);

    return selectIncompleteCapabilitiesFromCapabilities(state, appId, capabilities);
};

export const selectConnectedCapabilitiesForApp: (state: RootState, appId: string) => IntegrationCapability[] = (
    state,
    appId,
) => {
    const integrations = selectAppIntegrations(state, appId);

    const capabilityIntegrationCount = {};

    forEach(integrations, (integration) => {
        const caps = integration.capabilities;

        forEach(caps, (capability) => {
            const currCount = capabilityIntegrationCount[capability];

            if (currCount === undefined) {
                capabilityIntegrationCount[capability] = 1;
            } else {
                capabilityIntegrationCount[capability] = currCount + 1;
            }
        });
    });

    const connectedCapabilitiesForApp: IntegrationCapability[] = [];

    forEach(capabilityIntegrationCount, (value, key) => {
        if (value > 0) {
            connectedCapabilitiesForApp.push(key as IntegrationCapability);
        }
    });

    return connectedCapabilitiesForApp;
};

export const selectHasRequiredIntegrationsWithCapabilitiesInstalledForReleaseStep: (
    state: RootState,
    appId: string,
    releaseStepType: ReleaseStepType,
    onlyTheMinimumRequired?: boolean,
) => boolean = (state, appId, releaseStepType, onlyTheMinimumRequired = false) => {
    const capabilities = recommendedCapabilitiesForReleaseStepType(releaseStepType, onlyTheMinimumRequired);
    const connectCapsForApp = selectConnectedCapabilitiesForApp(state, appId);

    return every(capabilities, (capability) => {
        return connectCapsForApp.includes(capability);
    });
};

export const selectAppStoreIntegration: (state: RootState, appId: string) => Integration | undefined = (
    state,
    appId,
) => {
    const integrations = selectAppIntegrations(state, appId);
    const isRunwayOrg = selectIsRunwayOrg(state);
    const org = selectOrg(state)!;
    const allAppStoreTypes = allSupportedIntegrationTypesForCapability(
        IntegrationCapability.AppStore,
        isRunwayOrg,
        org.orgId,
    );

    return integrations.find((integration) => allAppStoreTypes.includes(integration.id));
};

export const selectBuildSystemIntegration: (state: RootState, appId: string) => Integration | undefined = (
    state,
    appId,
) => {
    return selectBuildSystemIntegrations(state, appId)[0];
};

export const selectBuildSystemIntegrations: (state: RootState, appId: string) => Integration[] = (state, appId) => {
    const integrations = selectAppIntegrations(state, appId);
    const isRunwayOrg = selectIsRunwayOrg(state);
    const org = selectOrg(state)!;
    const allBuildSystemTypes = allSupportedIntegrationTypesForCapability(
        IntegrationCapability.BuildSystem,
        isRunwayOrg,
        org.orgId,
    );

    return integrations.filter((integration) => allBuildSystemTypes.includes(integration.id));
};

export const selectAppStoreId = (
    state: RootState,
    appId: string,
    integrationType: IntegrationType,
): string | undefined => {
    const integration = selectIntegrationFromIntegrationType(state, appId, integrationType);
    switch (integrationType) {
        case IntegrationType.Apple: {
            return (integration as IntegrationApple)?.data.bundleId;
        }
        case IntegrationType.Google: {
            return (integration as IntegrationGoogle)?.data.packageName;
        }
        case IntegrationType.Amazon: {
            return (integration as IntegrationAmazon)?.data.selectedAppId;
        }
        case IntegrationType.HuaweiAppGallery: {
            return (integration as IntegrationHuawei)?.data.appId;
        }
        case IntegrationType.Samsung: {
            return (integration as IntegrationSamsung)?.data.appId;
        }
        default: {
            return undefined;
        }
    }
};

// This selector is intentionally not exported.
// If you need to access app users from a component, use the memoized selector `selectAppOrgUsers` found in memoizedSelectors.ts

const selectAppUsers: (state: RootState, appId: string) => OrgUser[] = (state, appId) => {
    const app = selectApp(state, appId);
    if (!app || !app.users) {
        return [];
    }
    const appUserIds = app.users;
    const orgUsers = selectOrgUsers(state);
    const orgUsersById = keyBy(orgUsers, (user) => user.id);

    return compact(appUserIds.map((userId) => orgUsersById[userId]));
};

// WARNING: this selector should be used very rarely. If you  need to get a specific org user, please use `selectOrgUser`
// Loading all org users can be very costly on orgs with a lot of users. Only use this selector if you actually need to display a list of org users

export const selectOrgUsers: (state: RootState, orgId?: string) => OrgUser[] = (state, orgId) => {
    if (_uiConfig(state).demoData?.orgUsersDemoData) {
        return Object.values(_uiConfig(state).demoData.orgUsersDemoData);
    }

    const org = selectOrg(state, orgId)!;
    return org.users;
};

export const selectAppUsersById: (state: RootState, appId: string, userIds: string[]) => OrgUser[] = (
    state,
    appId,
    userIds,
) => {
    const appUsers = selectAppUsers(state, appId);

    return appUsers.filter((user) => userIds.includes(user.id));
};

export const selectOrgUsersById: (state: RootState, orgId: string, userIds: string[]) => OrgUser[] | null = (
    state,
    orgId,
    userIds,
) => {
    const org = selectOrg(state, orgId)!;
    if (!userIds || userIds.length === 0) {
        return null;
    }
    return org.users.filter((user) => userIds.includes(user.id));
};

export const selectReleasePilotEligibleUsersForApp: (state: RootState, appId: string) => OrgUser[] = (state, appId) => {
    const appUsers = selectAppUsers(state, appId);

    return appUsers.filter((user) => user?.groups?.includes(DefaultUserGroupId.Pilot));
};

export const selectUserGroupsAndCommsGroupsMapByProviderId: (
    state: RootState,
    providerId: string,
    orgId: string,
) => UserGroupsToCommsGroupsMap | null | undefined = (state, providerId, orgId) => {
    const org = selectOrg(state, orgId)!;
    const providerIdToGroupsToCommsGroupsMap = org.orgSettings.providerIdToUserGroupsToCommsGroupsMap;
    return providerIdToGroupsToCommsGroupsMap ? providerIdToGroupsToCommsGroupsMap[providerId] : null;
};

export const selectProviderByProviderId: (
    state: RootState,
    orgId: string,
    providerId: string,
) => IntegrationProvider | undefined = (state, orgId, providerId) => {
    const providers = selectOrgProviders(state, orgId);
    return providers.find((provider) => provider.id === providerId);
};

export const selectOrgProviders: (state: RootState, orgId: string) => IntegrationProvider[] = (state, orgId) => {
    if (_uiConfig(state).demoData && _uiConfig(state).demoData.integrationProviders) {
        return _uiConfig(state).demoData.integrationProviders;
    }

    const org = selectOrg(state, orgId)!;

    return org.integrationProviders;
};

export const selectOrgProvidersById: (state: RootState, orgId: string) => Record<string, IntegrationProvider> = (
    state,
    orgId,
) => {
    const integrationProviders = selectOrgProviders(state, orgId);
    const integrationProviderById: Record<string, IntegrationProvider> = {};
    integrationProviders.forEach((provider) => {
        if (provider.id) {
            integrationProviderById[provider.id] = provider;
        }
    });
    return integrationProviderById;
};

export const selectAllAppGroupNames: (state: RootState) => string[] = (state) => {
    const appGroups = state.entities.appGroups.byId;

    return Object.values(appGroups).map((appGroup) => appGroup.name);
};

export const selectAllIntegrationsForCapabilities: (
    state: RootState,
    appId: string,
    capabilities: IntegrationCapability[],
) => IntegrationType[] = (state, appId, capabilities) => {
    const integrations: IntegrationType[] = [];

    capabilities.map((capability) => {
        switch (capability) {
            case IntegrationCapability.AppStore: {
                const appStoreIntegration = selectAppStoreIntegration(state, appId);
                if (appStoreIntegration) {
                    integrations.push(appStoreIntegration.id);
                }
                break;
            }
            case IntegrationCapability.FeatureManagement: {
                const featureManagementIntegration = selectFeatureManagementIntegration(state, appId);
                if (featureManagementIntegration) {
                    integrations.push(featureManagementIntegration.id);
                }
                break;
            }
            case IntegrationCapability.VersionControl: {
                const vcsIntegration = selectVCSIntegration(state, appId);
                if (vcsIntegration) {
                    integrations.push(vcsIntegration.id);
                }
                break;
            }
            case IntegrationCapability.BetaTesting: {
                const betaTestingIntegration = selectBetaTestingIntegration(state, appId);
                if (betaTestingIntegration) {
                    integrations.push(betaTestingIntegration.id);
                }
                break;
            }
            case IntegrationCapability.BuildSystem: {
                const buildSystemIntegration = selectBuildSystemIntegration(state, appId);

                if (buildSystemIntegration) {
                    integrations.push(buildSystemIntegration.id);
                }
                break;
            }
            case IntegrationCapability.SDKDistribution: {
                const sdkDistributionIntegration = selectSDKDistributionIntegration(state, appId);

                if (sdkDistributionIntegration) {
                    integrations.push(sdkDistributionIntegration.id);
                }
                break;
            }
            case IntegrationCapability.Notifications: {
                const notificationIntegration = selectNotificationIntegration(state, appId);

                if (notificationIntegration) {
                    integrations.push(notificationIntegration.id);
                }
                break;
            }
            case IntegrationCapability.StabilityMonitoring: {
                integrations.push(...selectStabilityIntegrations(state, appId).map((integration) => integration.id));
                break;
            }
            case IntegrationCapability.RegressionTesting: {
                const regressionTestingIntegration = selectRegressionTestingIntegration(state, appId);

                if (regressionTestingIntegration) {
                    integrations.push(regressionTestingIntegration.id);
                }
                break;
            }
            case IntegrationCapability.FeatureFlagging: {
                const featureFlaggingIntegration = selectFeatureFlaggingIntegration(state, appId);

                if (featureFlaggingIntegration) {
                    integrations.push(featureFlaggingIntegration.id);
                }

                break;
            }
            case IntegrationCapability.Translations: {
                const translationsIntegration = selectTranslationsIntegration(state, appId);

                if (translationsIntegration) {
                    integrations.push(translationsIntegration.id);
                }
                break;
            }
        }
    });

    return integrations;
};

export const selectIntegrationFromIntegrationType: (
    state: RootState,
    appId: string,
    integrationType: IntegrationType,
) => Integration | undefined = (state, appId, integrationType) => {
    const integrations = selectAppIntegrations(state, appId);
    return integrations?.find((integration) => integration.id === integrationType);
};

export const selectIntegrationsFromIntegrationInstallationIds: (
    state: RootState,
    appId: string,
    integrationInstallationIds: IntegrationInstallationId[],
) => Integration[] = (state, appId, integrationInstallationIds) => {
    const integrations = selectAppIntegrations(state, appId);
    return integrations?.filter((integration) => integrationInstallationIds.includes(integration.installationId));
};

export const selectChecklistItemsForStepRunCount: (state: RootState, stepRunId: string) => number = (
    state,
    stepRunId,
) => {
    return selectChecklistItemsForReleaseStepCount(state, '', null as unknown as ReleaseStepType, stepRunId);
};
export const selectChecklistItemsForReleaseStepCount: (
    state: RootState,
    releaseId: string,
    releaseStepType: ReleaseStepType,
    stepRunId?: string,
) => number = (state, releaseId, releaseStepType, stepRunId) => {
    const stepRunOrReleaseStep = selectStepRunOrReleaseStep(state, releaseId, releaseStepType, stepRunId);

    const checklistItems = stepRunOrReleaseStep.checklistItems;
    return checklistItems.length;
};

export const selectChecklistItemsForRolloutCount: (state: RootState, releaseId: string) => number = (
    state,
    releaseId,
) => {
    const rolloutMonitoring = selectRolloutMonitoring(state, releaseId);
    return rolloutMonitoring?.checklistItems?.length ?? 0;
};

export const selectCompletedChecklistItemsForStepRunCount: (state: RootState, stepRunId: string) => number = (
    state,
    stepRunId,
) => {
    return selectCompletedChecklistItemsForReleaseStepCount(state, '', null as unknown as ReleaseStepType, stepRunId);
};
export const selectCompletedChecklistItemsForReleaseStepCount: (
    state: RootState,
    releaseId: string,
    releaseStepType: ReleaseStepType,
    stepRunId?: string,
) => number = (state, releaseId, releaseStepType, stepRunId) => {
    const stepRunOrReleaseStep = selectStepRunOrReleaseStep(state, releaseId, releaseStepType, stepRunId);

    const checklistItems = stepRunOrReleaseStep.checklistItems;
    return computeCompletedChecklistItemsCount(checklistItems);
};

export const selectCompletedChecklistItemsForRolloutCount: (state: RootState, releaseId: string) => number = (
    state,
    releaseId,
) => {
    const rolloutMonitoring = selectRolloutMonitoring(state, releaseId);
    const checklistItems = rolloutMonitoring?.checklistItems ?? [];

    return computeCompletedChecklistItemsCount(checklistItems);
};

export const selectChecklistItem: (
    state: RootState,
    appId: string,
    checklistItemId: string,
) => ChecklistItemTemplate | undefined = (state, appId, checklistItemId) => {
    const appSettings = selectAppSettings(state, appId);

    return appSettings.stepChecklistItems.find((item) => item.id === checklistItemId);
};

export const selectChecklistItemsForReleaseStep: (
    state: RootState,
    releaseId: string,
    releaseStepType: ReleaseStepType,
) => ChecklistItem[] = (state, releaseId, releaseStepType) => {
    if (releaseStepType === ReleaseStepType.Rollout) {
        const rolloutMonitoring = selectRolloutMonitoring(state, releaseId);
        return rolloutMonitoring?.checklistItems ?? [];
    }

    const releaseStep = selectReleaseStep(state, releaseId, releaseStepType);

    return releaseStep.checklistItems;
};

export const selectChecklistItemsForStepRun: (state: RootState, stepRunId: string) => ChecklistItem[] = (
    state,
    stepRunId,
) => {
    const stepRun = selectStepRun(state, stepRunId);
    return stepRun.checklistItems;
};

export const selectShouldShowWelcomeModal: (state: RootState) => boolean = (state) => {
    return _uiConfig(state).welcomeModalOptions.shouldShowWelcomeModal;
};

export const selectShouldDirectlyShowNewAppFlow: (state: RootState) => boolean | undefined = (state) => {
    return _uiConfig(state).welcomeModalOptions.shouldDirectlyShowNewAppFlow;
};

export const selectStepChecklistItem: (
    state: RootState,
    releaseId: string,
    releaseStepType: ReleaseStepType,
    checklistItemId: string,
) => ChecklistItem | undefined = (state, releaseId, releaseStepType, checklistItemId) => {
    const items = selectChecklistItemsForReleaseStep(state, releaseId, releaseStepType);

    return items.find((item) => item.id === checklistItemId);
};

export const selectStepChecklistItemForStepRun: (
    state: RootState,
    stepRunId: string,
    checklistItemId: string,
) => ChecklistItem | undefined = (state, stepRunId, checklistItemId) => {
    const items = selectChecklistItemsForStepRun(state, stepRunId);

    return items.find((item) => item.id === checklistItemId);
};

// tracking

export const selectAllTrackingPropertiesForRelease: (
    state: RootState,
    orgId: string,
    appId: string,
    releaseId?: string,
) => StringKeyedDictionary<unknown> = (state, orgId, appId, releaseId) => {
    const org = selectOrg(state, orgId)!;
    const app = selectApp(state, appId);
    const release = releaseId ? selectRelease(state, releaseId) : undefined;
    const allAppIds = state.entities.apps.allIds;
    const appGroupsInOrg = selectAllAppGroupNames(state);

    const allProperties = allReleasePropertiesFrom(org, app, release, allAppIds, appGroupsInOrg);

    return allProperties;
};

export const selectWebhook: (state: RootState, orgId: string, webhookId: string) => WebhookEndpoint | undefined = (
    state,
    orgId,
    webhookId,
) => {
    const orgSettings = selectOrgSettings(state, orgId);

    return orgSettings.webhooks.find((webhook) => webhook.id === webhookId);
};

export const selectOrgSubscriptionInfo: (state: RootState, orgId: string) => OrgSubscriptionInfo = (state, orgId) => {
    const org = selectOrg(state, orgId)!;

    return org.subscriptionInfo;
};

// Pass subscriptionStatus to see whether the automation would normally be disabled for the given subscription status
// If no subscriptionStatus is passed, the disabled status of the automation for the current org will be returned
export const selectShouldDisableAutomation: (
    state: RootState,
    orgId: string,
    automationId: AutomationType,
    subscriptionStatus?: SubscriptionStatus,
) => boolean = (state, orgId, automationId, subscriptionStatus) => {
    const subscriptionInfo = selectOrgSubscriptionInfo(state, orgId);

    const featuresAndCapabilities =
        subscriptionStatus ?
            subscriptionInfo.featuresAndCapabilitiesPerSubscriptionStatus[subscriptionStatus]
        :   subscriptionInfo.currentFeaturesAndCapabilities;

    return !!featuresAndCapabilities.disabledAutomationsMap[automationId];
};

// Pass subscriptionStatus to see whether the integration capability would normally be disabled for the given subscription status
// If no subscriptionStatus is passed, the disabled status of the integration capability for the current org will be returned
export const selectShouldDisableIntegrationCapability: (
    state: RootState,
    orgId: string,
    integrationCapability: IntegrationCapability,
    subscriptionStatus?: SubscriptionStatus,
) => boolean = (state, orgId, integrationCapability, subscriptionStatus) => {
    const org = selectOrg(state, orgId)!;

    const featuresAndCapabilities =
        subscriptionStatus ?
            org.subscriptionInfo.featuresAndCapabilitiesPerSubscriptionStatus[subscriptionStatus]
        :   org.subscriptionInfo.currentFeaturesAndCapabilities;

    return !!featuresAndCapabilities.disabledIntegrationCapabilitiesMap[integrationCapability];
};

export const selectAppHasRequiredCapabilitiesAndIntegrationsForAutomation: (
    state: RootState,
    appId: string,
    unsupportedIntegrationTypes: IntegrationType[],
    requiredCapabilities: IntegrationCapability[][],
    integrationInstallationIds: IntegrationInstallationId[],
    isBucketAutomation?: boolean, // FIXME: remove after bucket automations have correct integration installation ids
) => [boolean, string] = (
    state,
    appId,
    unsupportedIntegrationTypes,
    requiredCapabilities,
    integrationInstallationIds,
    isBucketAutomation,
) => {
    const allAppIntegrations = selectAppIntegrations(state, appId);

    const integrationsConnectedToThisAutomation = allAppIntegrations.filter((integration) => {
        return integrationInstallationIds.includes(integration.installationId);
    });

    const connectedSupportedIntegrations = integrationsConnectedToThisAutomation.filter(
        (integration) => !unsupportedIntegrationTypes.includes(integration.id),
    );

    // if there are required capabilities, for each check if at least one of the connected integrations is supported
    // otherwise return the unsupported integrations error
    if (requiredCapabilities?.length > 0) {
        for (const capabilityGroup of requiredCapabilities) {
            for (const capability of capabilityGroup) {
                const connectedIntegrationsForRequiredCapability = integrationsConnectedToThisAutomation.filter(
                    (integration) => integration.capabilities.includes(capability),
                );

                const atLeastOneConnectedIntegrationIsSupported = connectedIntegrationsForRequiredCapability.some(
                    (integration) => !unsupportedIntegrationTypes.includes(integration.id),
                );

                // if at least one connected integration is supported, continue with the next required capability
                if (atLeastOneConnectedIntegrationIsSupported) {
                    break;
                }

                // otherwise, check if there are any unsupported integrations in the app for this required capability
                // if there are, that is the reason why they are not in in the integrationInstallationIds for this automation.
                const unsupportedAppIntegrationsForRequiredCapability = allAppIntegrations.filter(
                    (integration) =>
                        integration.capabilities.includes(capability) &&
                        unsupportedIntegrationTypes.includes(integration.id),
                );

                if (unsupportedAppIntegrationsForRequiredCapability.length > 0) {
                    const integrationName = displayNameForIntegrationType(
                        unsupportedAppIntegrationsForRequiredCapability[0].id,
                    );
                    return [false, `${integrationName} is not supported for this automation.`];
                }
            }
        }
    }

    const doesMeetIntegrationRequirements = requiredCapabilities?.every((interchangeableCapabilities) =>
        interchangeableCapabilities.some((capability) =>
            connectedSupportedIntegrations.some((integration) => integration.capabilities.includes(capability)),
        ),
    );

    const shouldOverrideForBDAutomations = isBucketAutomation && integrationInstallationIds.length === 0;

    if (shouldOverrideForBDAutomations || doesMeetIntegrationRequirements) {
        return [true, ''];
    } else {
        const unsupportedConnectedIntegrations = integrationsConnectedToThisAutomation.filter((integration) =>
            unsupportedIntegrationTypes.includes(integration.id),
        );

        if (unsupportedConnectedIntegrations.length > 0) {
            // this also may be a backend syncing issue
            const unsupportedIntegration = unsupportedConnectedIntegrations[0];
            const errorMessage = `Found unsupported integration ${unsupportedIntegration.id} in integrationInstallationIds. This may indicate a backend syncing issue.`;
            error(new Error(errorMessage));
        }

        const missingRequiredCapabilities: IntegrationCapability[] = [];
        for (const interchangeableCapabilities of requiredCapabilities) {
            const hasAnyCapability = interchangeableCapabilities.some((capability) =>
                connectedSupportedIntegrations.some((integration) => integration.capabilities.includes(capability)),
            );

            if (!hasAnyCapability) {
                missingRequiredCapabilities.push(interchangeableCapabilities[0]);
            }
        }

        if (missingRequiredCapabilities.length > 0) {
            const missingCapabilities = missingRequiredCapabilities.map((capability) =>
                displayNameForCapability(capability),
            );
            return [false, 'Missing required capabilities: ' + missingCapabilities.join(', ')];
        }
    }

    return [false, 'Configure required integration capabilities to enable this automation.'];
};

export const selectIsShowingTrialBanner: (state: RootState) => boolean = (state) => {
    const org = selectOrg(state)!;

    if (org.subscriptionInfo) {
        return org.subscriptionInfo.shouldShowTrialBanner;
    }

    return false;
};

export const selectIsTrialStarted: (state: RootState) => boolean = (state) => {
    const org = selectOrg(state)!;

    if (org.subscriptionInfo) {
        return org.subscriptionInfo?.isTrialStarted === true;
    }

    return false;
};

export const selectIsTrialEnded: (state: RootState) => boolean = (state) => {
    const org = selectOrg(state)!;

    if (org.subscriptionInfo) {
        return org.subscriptionInfo?.isTrialEnded === true;
    }

    return false;
};

export const selectTrialType: (state: RootState) => SubscriptionStatus | null | undefined = (state) => {
    const org = selectOrg(state)!;

    if (org.subscriptionInfo) {
        return org.subscriptionInfo?.trialType;
    }

    return null;
};

export const selectCurrentSubscriptionStatus: (state: RootState) => SubscriptionStatus = (state) => {
    const org = selectOrg(state)!;

    return org.subscriptionInfo.subscriptionStatus;
};

export const selectCurrentFeaturesAndCapabilities: (state: RootState) => SubscriptionFeaturesAndCapabilities = (
    state,
) => {
    const org = selectOrg(state)!;

    return org.subscriptionInfo.currentFeaturesAndCapabilities;
};

export const selectFeaturesAndCapabilitiesPerSubscriptionStatus: (
    state: RootState,
) => Record<SubscriptionStatus, SubscriptionFeaturesAndCapabilities> = (state) => {
    const org = selectOrg(state)!;
    return org.subscriptionInfo.featuresAndCapabilitiesPerSubscriptionStatus;
};

// Pass subscriptionStatus to see whether the feature would normally be disabled for the given subscription status
// If no subscriptionStatus is passed, the disabled status of the feature for the current org will be returned

export const selectShouldDisableFeature: (
    state: RootState,
    orgId: string,
    feature: SubscriptionFeatureType,
    subscriptionStatus?: SubscriptionStatus,
) => boolean = (state, orgId, feature, subscriptionStatus) => {
    const disabledOrLimitedFeature = selectDisabledFeatureData(state, orgId, feature, subscriptionStatus);

    if (!disabledOrLimitedFeature) {
        return false;
    }

    if (disabledOrLimitedFeature.isDisabled) {
        return true;
    }

    if (!disabledOrLimitedFeature.limit) {
        return false;
    }

    if (disabledOrLimitedFeature.limit && !isNil(disabledOrLimitedFeature.limitRemaining)) {
        return disabledOrLimitedFeature.limitRemaining === 0;
    }

    // conservative fallback
    return true;
};

export const selectDisabledFeatureData: (
    state: RootState,
    orgId: string,
    featureType: SubscriptionFeatureType,
    subscriptionStatus?: SubscriptionStatus,
) => DisabledOrLimitedSubscriptionFeature | undefined = (state, orgId, featureType, subscriptionStatus) => {
    const org = selectOrg(state, orgId)!;

    const featuresAndCapabilities =
        subscriptionStatus ?
            org.subscriptionInfo.featuresAndCapabilitiesPerSubscriptionStatus[subscriptionStatus]
        :   org.subscriptionInfo.currentFeaturesAndCapabilities;

    return featuresAndCapabilities.disabledOrLimitedFeaturesMap[featureType];
};

export const selectLastCompletedVersionTag: (state: RootState, appId: string) => ReleaseTag | undefined = (
    state,
    appId,
) => {
    const appSettings = selectAppSettings(state, appId);

    return appSettings.latestReleaseTag;
};

export const selectLatestVersion: (state: RootState, appId: string) => string | undefined = (state, appId) => {
    const appSettings = selectAppSettings(state, appId);

    return appSettings.latestCompletedVersion;
};

export const selectHasAtLeastNReleasesTagged: (state: RootState, appId: string, nReleases: number) => boolean = (
    state,
    appId,
    nReleases,
) => {
    const { entities } = state;

    let taggedReleasesCount = 0;

    const releases = Object.entries<Release>(entities.releases.byId);

    for (const [releaseId, release] of releases) {
        if (releaseId.startsWith(`${appId}:`) && release.isReleaseTagged) {
            taggedReleasesCount++;
        }

        if (taggedReleasesCount >= nReleases) {
            return true;
        }
    }

    return false;
};

export const selectIsDemoInstance: (state: RootState, orgId?: string) => boolean = (state, orgId) => {
    if (orgId) {
        return orgId === 'demo';
    } else {
        const currentOrg = selectOrg(state);

        return currentOrg?.id === 'demo';
    }
};

export const selectUserDevicesLoaded = (state: RootState): boolean => {
    return state.user?.devices !== undefined;
};

export const selectShouldShowRegisterDevicesPrompt: (
    state: RootState,
    devices: Device[],
    platform: AppPlatform,
    orgId: string,
) => boolean = (state, devices, platform, orgId) => {
    const isDemoInstance = selectIsDemoInstance(state, orgId);
    return !isDemoInstance && selectUserDevicesLoaded(state) && devices.length === 0 && getIsApplePlatform(platform);
};

export const selectReleasePilotForRelease: (
    state: RootState,
    appId: string,
    releaseId: string,
) => OrgUser | null | undefined = (state, appId: string, releaseId) => {
    const orgUsers = selectAppUsers(state, appId);
    const releasePilotId = selectRelease(state, releaseId).releasePilotId;

    if (releasePilotId) {
        return orgUsers.find((user) => user.id === releasePilotId);
    }

    return null;
};

export const selectAppHasIntegrationConnected: (
    state: RootState,
    appId: string,
    integrationType: IntegrationType,
) => boolean = (state, appId, integrationType) => {
    const integrations = selectAppIntegrations(state, appId);
    const integrationWithType = integrations.find((integration) => integration.id === integrationType);

    return !!integrationWithType;
};

export const selectJoinedCommsProvidersNamesList: (state: RootState) => string = (state) => {
    const isRunwayOrg = selectIsRunwayOrg(state);
    const org = selectOrg(state)!;
    const integrations = allSupportedIntegrationTypesForCapability(
        IntegrationCapability.Notifications,
        isRunwayOrg,
        org.orgId,
    );
    const integrationNames = integrations.map(displayNameForIntegrationType);

    if (integrationNames.length === 0) {
        return '';
    } else if (integrationNames.length === 1) {
        return integrationNames[0];
    } else if (integrationNames.length === 2) {
        return `${integrationNames[0]} or ${integrationNames[1]}`;
    } else {
        const lastIntegration = integrationNames[length - 1];
        const otherIntegrations = integrationNames.slice(0, length - 1).join(', ');
        return `${otherIntegrations}, or ${lastIntegration}`;
    }
};

export const selectAppCenterDistributionGroupsUrl: (state: RootState, appId: string) => string = (state, appId) => {
    const integration = selectIntegrationFromIntegrationType(state, appId, IntegrationType.AppCenterBeta);
    const data = integration?.data as AppCenterBetaIntegrationData;
    return data?.consoleUrl ?? 'https://appcenter.ms';
};

export const selectFirebaseAppDistributionConsoleUrl: (state: RootState, appId: string) => string = (state, appId) => {
    const integration = selectIntegrationFromIntegrationType(state, appId, IntegrationType.FirebaseBeta);
    const data = integration?.data as FirebaseBetaIntegrationData;
    return data?.consoleUrl ?? 'https://console.firebase.google.com';
};

export const selectTestFlightUrl: (state: RootState, appId: string) => string = (state, appId) => {
    const integration = selectIntegrationFromIntegrationType(state, appId, IntegrationType.TestFlight);
    const data = integration?.data as TestFlightIntegrationData;
    return data?.consoleUrl ?? 'https://appstoreconnect.apple.com';
};

export const selectGoogleBetaUrl: (state: RootState, appId: string) => string = (state, appId) => {
    const integration = selectIntegrationFromIntegrationType(state, appId, IntegrationType.GoogleBeta);
    const data = integration?.data as GoogleBetaIntegrationData;
    const testingTrack =
        getTestingTrackFromAutoPromoteBuildToTestTrackAutomationWithState(state, appId) ?? data.testingTrack;

    if (data?.consoleUrl && testingTrack) {
        return `${data.consoleUrl}/${testingTrack}`;
    }

    return 'https://play.google.com/console';
};

export const selectBetaIntegrationLinkUrl: (
    state: RootState,
    appId: string,
    integrationType: IntegrationType,
) => string | null = (state, appId, integrationType) => {
    switch (integrationType) {
        case IntegrationType.TestFlight:
            return selectTestFlightUrl(state, appId);
        case IntegrationType.AppCenterBeta:
            return selectAppCenterDistributionGroupsUrl(state, appId);
        case IntegrationType.GoogleBeta:
            return selectGoogleBetaUrl(state, appId);
        case IntegrationType.FirebaseBeta:
            return selectFirebaseAppDistributionConsoleUrl(state, appId);
        default:
            return null;
    }
};

/* returns true if the beta integration type doesn't match the relevant app store integration for the platform
OR
   the bundle identifier / package names of the two integrations don't match.

in other words, returns true if beta testing builds are not the same as app store / production builds
*/
export const selectHasNonAppStoreBetaIntegration: (state: RootState, appId: string) => boolean = (state, appId) => {
    const betaIntegration = selectIntegrationForCapability(state, appId, IntegrationCapability.BetaTesting);

    if (betaIntegration) {
        const betaIntegrationIsAppStore = betaIntegrationTypeIsAppStore(betaIntegration.id);

        if (betaIntegrationIsAppStore) {
            // account for cases where the beta integration matches an app store integration, but the bundle identifiers/package names don't match
            const appStoreIntegration = selectIntegrationForCapability(state, appId, IntegrationCapability.AppStore);

            if (betaIntegration && appStoreIntegration) {
                switch (appStoreIntegration.id) {
                    case IntegrationType.Apple:
                        return (
                            (betaIntegration.data as TestFlightIntegrationData).bundleId !==
                            (appStoreIntegration.data as AppleIntegrationData).bundleId
                        );
                    case IntegrationType.Google:
                        return (
                            (betaIntegration.data as GoogleBetaIntegrationData).packageName !==
                            (appStoreIntegration.data as GoogleIntegrationData).packageName
                        );
                }
            }
        }

        return !betaIntegrationIsAppStore;
    }

    return false;
};

export const selectMonitoringMetricsById: (
    state: RootState,
    appId: string,
) => { [key: string]: AppMonitoringHealthMetric } = (state, appId) => {
    const appSettings = selectAppSettings(state, appId);

    return keyBy(appSettings.monitoringSettings.metrics, (metric) => metric.id);
};

export const selectMonitoringMetricsByCategory: (
    state: RootState,
    appId: string,
    category: AppMonitoringHealthMetricCategory,
) => AppMonitoringHealthMetric[] = (state, appId, category) => {
    const metrics = selectAppSettings(state, appId).monitoringSettings.metrics;

    return metrics.filter((metric) => metric.category === category);
};

export const selectIsLatestCompletedRelease: (state: RootState, appId: string, releaseId: string) => boolean = (
    state,
    appId,
    releaseId,
) => {
    const releases = getAllReleasesForAppId(appId, state);

    const sortedCompletedReleases = releases
        ?.filter((release) => release.timelinePhase === ReleaseTimelinePhase.Completed)
        .sort((releaseA, releaseB) => compareVersions(releaseA.version, releaseB.version));

    if (sortedCompletedReleases && sortedCompletedReleases.length != 0) {
        return sortedCompletedReleases[0].id === releaseId;
    }

    return false;
};

export const selectIsRolloutPaused: (state: RootState, appId: string, releaseId: string) => boolean | null = (
    state,
    appId,
    releaseId,
) =>
    selectIsLatestCompletedRelease(state, appId, releaseId) ?
        (() => {
            const release = selectRelease(state, releaseId);
            return release?.phasedRelease?.state ? isPhasedReleasePaused(release.phasedRelease.state) : false;
        })()
    :   null;

export const selectIsRolloutPausedByRunway: (state: RootState, appId: string, releaseId: string) => boolean = (
    state,
    appId,
    releaseId,
) => {
    if (!selectIsLatestCompletedRelease(state, appId, releaseId)) {
        return false;
    }
    const release = selectRelease(state, releaseId);
    return !!release?.phasedRelease?.isRunwayPaused;
};

export const selectStatisticsById: (state: RootState, id: string) => OrgOverviewStatistic[] = (state, id) => {
    if (_uiConfig(state).demoData?.statistics) {
        if (id.endsWith('ios')) {
            return _uiConfig(state).demoData.statistics['app-apollo-ios'];
        } else if (id.endsWith('android')) {
            return _uiConfig(state).demoData.statistics['app-apollo-android'];
        }
        return _uiConfig(state).demoData.statistics['org-demo'];
    }
    return state.entities.statistics.byId[id]?.statistics ?? null;
};

export const selectOrgOverviewAverageApprovalItemDurations: (
    state: RootState,
    id: string,
) => OrgOverviewApprovalItemDurations | null = (state, id) => {
    return state.entities.statistics.byId[id]?.averageApprovalItemDurations ?? null;
};

export const selectIsReleaseFromScraper: (state: RootState, releaseId: string) => boolean = (state, releaseId) => {
    const release = selectRelease(state, releaseId);

    return release.createdOrigin === ReleaseCreatedOrigin.AppStoreScraper;
};

export const selectProviderForIntegration: (
    state: RootState,
    orgId: string,
    integration: Integration,
) => IntegrationProvider | null = (state, orgId, integration) => {
    const integrationProviders = selectOrgProviders(state, orgId);
    const provider = integrationProviders.find((provider) => provider.id === integration?.providerId);
    return provider ?? null;
};

export const selectAppIdentifiersAndPlatformsFromAppIds: (
    state: RootState,
    appIds: string[],
) => AppIdentifiersAndPlatform[] = (state, appIds) => {
    return compact(
        appIds.map((appId) => {
            const app = selectApp(state, appId);
            if (!app?.appSettings) {
                return null;
            }
            return {
                appId: appId,
                appName: app.appSettings.appName,
                platform: app.appSettings.platform,
            };
        }),
    );
};

export const selectOrgSubscriptionPlanText: (state: RootState, orgId: string) => string | null = (state, orgId) => {
    const subscriptionInfo = selectOrg(state, orgId)?.subscriptionInfo;

    if (subscriptionInfo) {
        if (
            subscriptionInfo.shouldShowTrialBanner &&
            subscriptionInfo.isTrialStarted &&
            !subscriptionInfo.isTrialEnded
        ) {
            return `${displaySubscriptionStatus(subscriptionInfo.trialType, false)} Plan –– Free trial`;
        }

        return `${displaySubscriptionStatus(subscriptionInfo.subscriptionStatus, true)} Plan`;
    }

    return null;
};

export const selectAppsById: (state: RootState, appIds: string[]) => AppEntity[] = (state, appIds) => {
    const apps = appIds.map((appId) => selectApp(state, appId));

    // filter out null values
    return compact(apps);
};

export const selectPaginatedReleasesLimitAndOffset: (
    state: RootState,
    appId: string,
) => { limit: number; offset: number; totalCount: number } = (state, appId) => {
    const releasePagination = _uiConfig(state).releasesPagination[appId];

    return {
        limit: releasePagination?.limit ?? null,
        offset: releasePagination?.offset ?? null,
        totalCount: releasePagination?.totalCount ?? null,
    };
};

export const selectAppIssues = (state: RootState, appId: string) => state.entities.issues[appId]?.issues ?? [];

export const selectAppIssueById = (state: RootState, appId: string, issueId: string) => {
    const forApp = state.entities.issues[appId];
    if (!forApp) return undefined;
    return forApp.issues.find((i) => i.id === issueId) ?? forApp.coldLoadedById?.[issueId];
};

export const selectIssuesCounts = (state: RootState, appId: string) => ({
    openCount: state.entities.issues[appId]?.openCount ?? 0,
    closedCount: state.entities.issues[appId]?.closedCount ?? 0,
});

export const selectIssueTimeline = (state: RootState, issueId: string) => state.entities.issueTimelines[issueId] ?? [];

export const selectPaginatedIssuesLimitAndOffset = (state: RootState, appId: string) => {
    const issuesPagination = _uiConfig(state).issuesPagination[appId];
    return {
        limit: issuesPagination?.limit ?? null,
        offset: issuesPagination?.offset ?? null,
    };
};

export const selectTriageFilters = (state: RootState, appId: string) =>
    state.triageFilters[appId] ?? DEFAULT_TRIAGE_FILTERS;

export const selectPaginatedBucketBuildsLimitAndOffset: (
    state: RootState,
    bucketId: string,
) => { limit: number; offset: number } = (state, bucketId) => {
    if (_uiConfig(state).demoData?.buildDistroDemoData) {
        return {
            limit: 0,
            offset: 0,
        };
    }

    const bucketBuildsPagination = _uiConfig(state).bucketBuildsPagination[bucketId];

    return {
        limit: bucketBuildsPagination?.limit,
        offset: bucketBuildsPagination?.offset,
    };
};

export const selectPaginatedSharedWithMeBuildsLimitAndOffset: (
    state: RootState,
    appId: string,
) => { limit: number; offset: number } = (state, appId) => {
    if (_uiConfig(state).demoData?.buildDistroDemoData) {
        return {
            limit: 0,
            offset: 0,
        };
    }

    const sharedWithMeBuildsPagination = _uiConfig(state).sharedWithMeBuildsPagination[appId];

    return {
        limit: sharedWithMeBuildsPagination?.limit,
        offset: sharedWithMeBuildsPagination?.offset,
    };
};

export const selectPaginatedBucketBuildWorkItemsTimelineLimitOffsetAndTotalCount: (
    state: RootState,
    buildId: string,
) => { limit: number; offset: number; totalCount: number } = (state, buildId) => {
    if (_uiConfig(state).demoData?.buildDistroDemoData) {
        return {
            limit: 0,
            offset: 0,
            totalCount: 0,
        };
    }

    const bucketBuildsWorkItemsTimelinePagination = _uiConfig(state).bucketBuildsWorkItemsTimelinePagination[buildId];

    return {
        limit: bucketBuildsWorkItemsTimelinePagination?.limit ?? 0,
        offset: bucketBuildsWorkItemsTimelinePagination?.offset ?? 0,
        totalCount: bucketBuildsWorkItemsTimelinePagination?.totalCount ?? 0,
    };
};

export const getPaginatedEntityId = (type: string, id: string) => {
    return `${type}#${id}`;
};

export const selectPaginationData: (
    state: RootState,
    type: FetchableEntityTypeV2,
    id: string,
) => CommonSagaActionsData['paginationData'] = (state, type, id) => {
    const entityId = getPaginatedEntityId(type, id);
    return _uiConfig(state).paginationData[entityId];
};

const getAppStoreIntegrationsForDestinationSteps: (
    step: StepGraphStep,
    stepConfigMap: Record<string, StepGraphStep>,
    appIntegrations: Integration[],
) => Integration[] = (step, stepConfigMap, appIntegrations) => {
    if (!step?.destinationStepConfigIds?.length) {
        return [];
    }

    const uniqueIntegrations = new Map<IntegrationInstallationId, Integration>();

    const destinationStepConfigIds = step.destinationStepConfigIds ?? [];
    for (const destinationStepConfigId of destinationStepConfigIds) {
        const destinationStepConfig = stepConfigMap[destinationStepConfigId];
        const integrationIds = destinationStepConfig?.integrationInstallationIds ?? [];

        for (const installationId of integrationIds) {
            const matchingIntegration = appIntegrations.find(
                (integration) => integration.installationId === installationId,
            );
            if (matchingIntegration && matchingIntegration.capabilities.includes(IntegrationCapability.AppStore)) {
                uniqueIntegrations.set(matchingIntegration.installationId, matchingIntegration);
            }
        }
    }

    return Array.from(uniqueIntegrations.values());
};

const getBetaIntegrationsForDestinationSteps: (
    destinationStepIds: string[],
    stepConfigMap: Record<string, StepGraphStep>,
    appIntegrations: Integration[],
) => Integration[] = (destinationStepIds, stepConfigMap, appIntegrations) => {
    if (!destinationStepIds?.length) {
        return [];
    }

    const destinationIdsSet = new Set(destinationStepIds);
    const uniqueIntegrations = new Map<IntegrationInstallationId, Integration>();

    for (const step of Object.values(stepConfigMap)) {
        const betaStepDestinations = step.destinationStepConfigIds ?? [];
        const sharesDestination = betaStepDestinations.some((destinationId) => destinationIdsSet.has(destinationId));

        if (!sharesDestination) {
            continue;
        }

        const integrationIds = step.integrationInstallationIds ?? [];

        for (const installationId of integrationIds) {
            const matchingIntegration = appIntegrations.find(
                (integration) => integration.installationId === installationId,
            );

            if (matchingIntegration && matchingIntegration.capabilities.includes(IntegrationCapability.BetaTesting)) {
                uniqueIntegrations.set(matchingIntegration.installationId, matchingIntegration);
            }
        }
    }

    return Array.from(uniqueIntegrations.values());
};

export const selectAppStoreArtifactWorkflowUploadDestinationOptions: (
    state: RootState,
    appId: string,
    automationId: string,
) => AppStoreArtifactUploadDestinationTypeOption[] = (state, appId, automationId) => {
    const automation = selectAutomationById(state, appId, automationId);
    let appStoreIntegrationsForDestinations: Integration[] = [];
    let betaIntegrationsSharingSameDestinations: Integration[] = [];

    if (automation && typeof automation.stepConfigId === 'string') {
        const stepConfigMap = selectStepConfigDataFromStepGraphs(state, appId);
        const stepGraphStep = stepConfigMap[automation.stepConfigId];
        const integrations = selectAppIntegrations(state, appId);
        if (stepGraphStep) {
            const destinationStepConfigIds = stepGraphStep.destinationStepConfigIds ?? [];
            appStoreIntegrationsForDestinations = getAppStoreIntegrationsForDestinationSteps(
                stepGraphStep,
                stepConfigMap,
                integrations,
            );
            betaIntegrationsSharingSameDestinations = getBetaIntegrationsForDestinationSteps(
                destinationStepConfigIds,
                stepConfigMap,
                integrations,
            );
        }
    }
    let integrationInstallationIds: string[];
    if (!automation || !automation.integrationInstallationIds) {
        // workaround for bucket automations
        const integrations = selectAppIntegrations(state, appId);
        integrationInstallationIds = integrations.map((integration) => integration.installationId);
    } else {
        integrationInstallationIds = automation.integrationInstallationIds;
    }

    const platform = selectAppPlatform(state, appId);
    const mergeIntegrationsByInstallationId = (...integrationLists: Integration[][]): Integration[] => {
        const integrationMap = new Map<IntegrationInstallationId, Integration>();
        for (const list of integrationLists) {
            for (const integration of list) {
                if (integration) {
                    integrationMap.set(integration.installationId, integration);
                }
            }
        }
        return Array.from(integrationMap.values());
    };

    const betaIntegrationsFromAutomation = selectIntegrationsWithCapabilityFromInstallations(
        state,
        appId,
        IntegrationCapability.BetaTesting,
        integrationInstallationIds,
    );
    const appStoreIntegrationsFromAutomation = selectIntegrationsWithCapabilityFromInstallations(
        state,
        appId,
        IntegrationCapability.AppStore,
        integrationInstallationIds,
    );
    const betaIntegrations = mergeIntegrationsByInstallationId(
        betaIntegrationsFromAutomation,
        betaIntegrationsSharingSameDestinations,
    );
    const appStoreIntegrations = mergeIntegrationsByInstallationId(
        appStoreIntegrationsFromAutomation,
        appStoreIntegrationsForDestinations,
    );

    const destinationOptions: AppStoreArtifactUploadDestinationTypeOption[] = [];
    for (const betaTesting of betaIntegrations) {
        switch (betaTesting.id) {
            case IntegrationType.TestFlight: {
                const bundleId = betaTesting.data?.bundleId;
                if (bundleId) {
                    const testFlightOption: AppStoreArtifactUploadDestinationTypeOption = {
                        id: betaTesting.installationId,
                        type: AppStoreArtifactUploadDestinationType.DestinationBetaTesting,
                        name: betaTesting.name,
                        alias: betaTesting.alias ?? '',
                        identifier: betaTesting.data?.bundleId ?? '',
                    };
                    destinationOptions.push(testFlightOption);
                }
                break;
            }
            case IntegrationType.GoogleBeta: {
                const packageName = betaTesting.data?.packageName;
                if (packageName) {
                    const availableTracks = betaTesting.data?.availableTracks?.filter((track) => {
                        return filterTrackByPlatform(track, platform);
                    });

                    const googleBetaOption: AppStoreArtifactUploadDestinationTypeOption = {
                        id: betaTesting.installationId,
                        type: AppStoreArtifactUploadDestinationType.DestinationBetaTesting,
                        name: betaTesting.name,
                        alias: betaTesting.alias ?? '',
                        identifier: betaTesting.data?.packageName ?? '',
                        availableTracks: availableTracks,
                    };
                    destinationOptions.push(googleBetaOption);
                }
                break;
            }
            case IntegrationType.FirebaseBeta: {
                const appIdentifier = betaTesting.data?.appIdentifier;
                if (appIdentifier) {
                    const firebaseOption: AppStoreArtifactUploadDestinationTypeOption = {
                        id: betaTesting.installationId,
                        type: AppStoreArtifactUploadDestinationType.DestinationBetaTesting,
                        name: betaTesting.name,
                        alias: betaTesting.alias ?? '',
                        identifier: appIdentifier,
                    };
                    destinationOptions.push(firebaseOption);
                }
                break;
            }
            case IntegrationType.AppCenterBeta: {
                const appIdentifier = betaTesting.data?.selectedApp?.id;
                if (appIdentifier) {
                    const appCenterOption: AppStoreArtifactUploadDestinationTypeOption = {
                        id: betaTesting.installationId,
                        type: AppStoreArtifactUploadDestinationType.DestinationBetaTesting,
                        name: betaTesting.name,
                        alias: betaTesting.alias ?? '',
                        identifier: appIdentifier,
                    };
                    destinationOptions.push(appCenterOption);
                }
                break;
            }
        }
    }

    for (const appStore of appStoreIntegrations) {
        switch (appStore.id) {
            case IntegrationType.Apple: {
                const bundleId = appStore.data?.bundleId;
                if (bundleId) {
                    const appStoreOption: AppStoreArtifactUploadDestinationTypeOption = {
                        id: appStore.installationId,
                        type: AppStoreArtifactUploadDestinationType.DestinationAppStore,
                        name: appStore.name,
                        alias: appStore.alias ?? '',
                        identifier: appStore.data?.bundleId ?? '',
                    };
                    destinationOptions.push(appStoreOption);
                }
                break;
            }
            case IntegrationType.Google: {
                const packageName = appStore.data?.packageName;
                if (packageName) {
                    const availableTracks = appStore.data?.availableTracks?.filter((track) =>
                        filterTrackByPlatform(track, platform),
                    );

                    const googlePlayOption: AppStoreArtifactUploadDestinationTypeOption = {
                        id: appStore.installationId,
                        type: AppStoreArtifactUploadDestinationType.DestinationAppStore,
                        name: appStore.name,
                        alias: appStore.alias ?? '',
                        identifier: appStore.data?.packageName ?? '',
                        availableTracks: availableTracks,
                    };
                    destinationOptions.push(googlePlayOption);
                }
                break;
            }
            case IntegrationType.Amazon: {
                const appId = appStore.data?.selectedAppId;
                if (appId) {
                    const amazonOption = {
                        id: appStore.installationId,
                        type: AppStoreArtifactUploadDestinationType.DestinationAppStore,
                        name: appStore.name,
                        alias: appStore.alias,
                        identifier: appStore.data?.selectedAppId,
                    };
                    destinationOptions.push(amazonOption);
                }
                break;
            }
            case IntegrationType.HuaweiAppGallery: {
                const huaweiAppId = appStore.data?.appId;
                if (huaweiAppId) {
                    const huaweiOption = {
                        id: appStore.installationId,
                        type: AppStoreArtifactUploadDestinationType.DestinationAppStore,
                        name: appStore.name,
                        alias: appStore.alias,
                        identifier: huaweiAppId,
                    };
                    destinationOptions.push(huaweiOption);
                }
                break;
            }
            case IntegrationType.Samsung: {
                const samsungAppId = appStore.data?.appId;
                if (samsungAppId) {
                    const samsungOption = {
                        id: appStore.installationId,
                        type: AppStoreArtifactUploadDestinationType.DestinationAppStore,
                        name: appStore.name,
                        alias: appStore.alias,
                        identifier: samsungAppId,
                    };
                    destinationOptions.push(samsungOption);
                }
                break;
            }
        }
    }
    return destinationOptions;
};

export const selectAllAppIdentifiers: (
    state: RootState,
    appId: string,
) => Map<IntegrationInstallationId, AppIdentifierForIntegration> = (state, appId) => {
    const appIdentifiers = new Map<IntegrationInstallationId, AppIdentifierForIntegration>([]);
    const connectedCapabilities = selectConnectedCapabilitiesForApp(state, appId);
    if (connectedCapabilities.includes(IntegrationCapability.BetaTesting)) {
        const betaIntegrations = selectIntegrationsForCapability(state, appId, IntegrationCapability.BetaTesting);
        for (const betaTesting of betaIntegrations) {
            switch (betaTesting.id) {
                case IntegrationType.TestFlight:
                    appIdentifiers.set(betaTesting.installationId, {
                        capability: IntegrationCapability.BetaTesting,
                        installationId: betaTesting.installationId,
                        appIdentifier: betaTesting.data?.bundleId ?? '',
                    });
                    break;
                case IntegrationType.GoogleBeta:
                    appIdentifiers.set(betaTesting.installationId, {
                        capability: IntegrationCapability.BetaTesting,
                        installationId: betaTesting.installationId,
                        appIdentifier: betaTesting.data?.packageName ?? '',
                    });
                    break;
            }
        }
    }
    if (connectedCapabilities.includes(IntegrationCapability.AppStore)) {
        const appStore = selectAppStoreIntegration(state, appId);
        if (appStore) {
            switch (appStore.id) {
                case IntegrationType.Apple:
                    appIdentifiers.set(appStore.installationId, {
                        capability: IntegrationCapability.AppStore,
                        installationId: appStore.installationId,
                        appIdentifier: appStore.data?.bundleId ?? '',
                    });
                    break;
                case IntegrationType.Google:
                    appIdentifiers.set(appStore.installationId, {
                        capability: IntegrationCapability.AppStore,
                        installationId: appStore.installationId,
                        appIdentifier: appStore.data?.packageName ?? '',
                    });
                    break;
                case IntegrationType.Amazon:
                    appIdentifiers.set(appStore.installationId, {
                        capability: IntegrationCapability.AppStore,
                        installationId: appStore.installationId,
                        appIdentifier: appStore.data?.selectedAppId ?? '',
                    });
                    break;
                case IntegrationType.HuaweiAppGallery:
                    appIdentifiers.set(appStore.installationId, {
                        capability: IntegrationCapability.AppStore,
                        installationId: appStore.installationId,
                        appIdentifier: appStore.data?.appId ?? '',
                    });
                    break;
                case IntegrationType.Samsung:
                    appIdentifiers.set(appStore.installationId, {
                        capability: IntegrationCapability.AppStore,
                        installationId: appStore.installationId,
                        appIdentifier: appStore.data?.appId ?? '',
                    });
                    break;
            }
        }
    }
    return appIdentifiers;
};

// Deprecated: use selectAllAppIdentifiers instead
export const selectAppIdentifiers: (state: RootState, appId: string) => Map<IntegrationCapability, string> = (
    state,
    appId,
) => {
    const appIdentifiers = new Map<IntegrationCapability, string>([]);
    const connectedCapabilities = selectConnectedCapabilitiesForApp(state, appId);
    if (connectedCapabilities.includes(IntegrationCapability.BetaTesting)) {
        const betaTesting = selectBetaTestingIntegration(state, appId);
        if (betaTesting) {
            switch (betaTesting.id) {
                case IntegrationType.TestFlight:
                    appIdentifiers[IntegrationCapability.BetaTesting] = betaTesting.data?.bundleId;
                    break;
                case IntegrationType.GoogleBeta:
                    appIdentifiers[IntegrationCapability.BetaTesting] = betaTesting.data?.packageName;
                    break;
            }
        }
    }
    if (connectedCapabilities.includes(IntegrationCapability.AppStore)) {
        const appStore = selectAppStoreIntegration(state, appId);
        if (appStore) {
            switch (appStore.id) {
                case IntegrationType.Apple:
                    appIdentifiers[IntegrationCapability.AppStore] = appStore.data?.bundleId;
                    break;
                case IntegrationType.Google:
                    appIdentifiers[IntegrationCapability.AppStore] = appStore.data?.packageName;
                    break;
                case IntegrationType.Amazon:
                    appIdentifiers[IntegrationCapability.AppStore] = appStore.data?.selectedAppId;
                    break;
                case IntegrationType.HuaweiAppGallery:
                    appIdentifiers[IntegrationCapability.AppStore] = appStore.data?.appId;
                    break;
                case IntegrationType.Samsung:
                    appIdentifiers[IntegrationCapability.AppStore] = appStore.data?.appId;
                    break;
            }
        }
    }
    return appIdentifiers;
};

export const selectSharedWithMeBuildIds: (state: RootState, appId: string) => string[] = (state, appId) => {
    const demoData = _uiConfig(state).demoData?.buildDistroDemoData?.sharedWithMeBuildIdsForAppId?.[appId];
    return demoData ?? state.entities.buildDistribution.sharedWithMeBuildIdsForAppId[appId] ?? [];
};

export const selectSharedWithMeBuildsCount: (state: RootState, appId: string) => number | undefined = (
    state,
    appId,
) => {
    const demoData = _uiConfig(state).demoData?.buildDistroDemoData?.sharedWithMeTotalCountsForAppId?.[appId];
    return demoData ?? state.entities.buildDistribution.sharedWithMeTotalCountsForAppId[appId];
};

export const selectBuildDistributionBucketsForAppId: (
    state: RootState,
    appId: string,
) => (BuildDistributionBucket | null | undefined)[] | undefined = (state, appId) => {
    const demoData = _uiConfig(state).demoData?.buildDistroDemoData?.bucketIdsForAppId?.[appId];
    const bucketIdsForAppId = demoData ?? state.entities.buildDistribution.bucketIdsForAppId[appId];

    return bucketIdsForAppId?.map((bucketId) => selectBuildDistributionBucketById(state, bucketId));
};

export const selectBuildDistributionBuildById: (state: RootState, buildId: string) => BuildDistroBuild = (
    state,
    buildId,
) => {
    const demoData = _uiConfig(state).demoData?.buildDistroDemoData?.buildsById?.[buildId];

    return demoData ?? state.entities.buildDistribution.buildsById[buildId];
};

export const selectBuildDistributionBucketById: (
    state: RootState,
    bucketId: string,
) => BuildDistributionBucket | null | undefined = (state, bucketId) => {
    if (!bucketId) {
        return null;
    }

    const demoData = _uiConfig(state).demoData?.buildDistroDemoData?.bucketsById?.[bucketId];

    return demoData ?? state.entities.buildDistribution.bucketsById[bucketId];
};

export const selectBuildDistributionDownloadMetricsByBucketId: (
    state: RootState,
    bucketId: string,
) => BuildDistroBucketDownloadMetrics | null | undefined = (state, bucketId) => {
    if (!bucketId) {
        return null;
    }

    const demoData = _uiConfig(state).demoData?.buildDistroDemoData?.downloadMetricsForBucketId?.[bucketId];

    return demoData ?? state.entities.buildDistribution.downloadMetricsForBucketId[bucketId];
};

export const selectBuildDistributionBucketBuildsByIds: (state: RootState, buildIds: string[]) => BuildDistroBuild[] = (
    state,
    buildIds,
) => {
    return buildIds.map((buildId) => selectBuildDistributionBuildById(state, buildId));
};

export const selectShouldShowFeatureFlags: (state: RootState, orgId: string, appId: string) => boolean = (
    state,
    orgId,
    appId,
) => {
    const isRunwayAdmin = selectUserIsRunwayAdmin(state);

    if (orgId === 'peloton' && appId === 'peloton-ios') {
        // only admins see feature flags on peloton iOS
        return selectUserIsAdmin(state) || isRunwayAdmin;
    }

    return true;
};

export const selectRunwayAdminWebsocketsEnabled: (state: RootState) => boolean = (state) => {
    return _uiConfig(state).runwayAdminWebsocketsEnabled;
};

export const selectReleasePilotRotation: (state: RootState, appId: string) => ReleasePilotRotation = (state, appId) => {
    const appSettings = selectAppSettings(state, appId);
    return appSettings.releasePilotRotation;
};

export const selectAppDevices = (state: RootState, appId: string): AppDevice[] | undefined => {
    if (
        _uiConfig(state).demoData &&
        _uiConfig(state).demoData.appsDemoData &&
        _uiConfig(state).demoData.appsDemoData[appId]
    ) {
        return _uiConfig(state).demoData.appsDemoData[appId].app.devices;
    }

    const app = selectApp(state, appId);
    return app.devices;
};

export const selectUserDevices = (state: RootState): Device[] => {
    return state.user?.devices ?? [];
};

export const selectUserEmailNotificationSettings = (state: RootState): UserEmailNotificationSettings | undefined => {
    return state.user.emailNotificationSettings;
};

export const selectDevicesRegisteredInASCCount = (state: RootState, appId: string): number | undefined => {
    const app = selectApp(state, appId);
    return app.devicesRegisteredInASCCount;
};

export const selectAppProvisioningProfiles = (state: RootState, appId: string): ProvisioningProfile[] | undefined => {
    const app = selectApp(state, appId);
    return app.provisioningProfiles;
};

export const selectShouldDisableFeatureIntegrationCapabilityOrAutomationForSubscriptionType: (
    state: RootState,
    subscriptionStatus: SubscriptionStatus,
    feature?: SubscriptionFeatureType,
    automationType?: AutomationType,
    integrationCapability?: IntegrationCapability,
) => boolean = (
    state: RootState,
    subscriptionStatus: SubscriptionStatus,
    feature?: SubscriptionFeatureType,
    automationType?: AutomationType,
    integrationCapability?: IntegrationCapability,
) => {
    const orgId = selectOrg(state)!.id;

    if (feature) {
        return selectShouldDisableFeature(state, orgId, feature, subscriptionStatus);
    } else if (integrationCapability) {
        return selectShouldDisableIntegrationCapability(state, orgId, integrationCapability, subscriptionStatus);
    } else if (automationType) {
        return selectShouldDisableAutomation(state, orgId, automationType, subscriptionStatus);
    } else {
        return false;
    }
};

export const selectAllOpenPRsAgainstWorkingBranch: (state: RootState, stepRunId: string) => FeatureItem[] = (
    state,
    stepRunId,
) => {
    const hasFeatureWorkItemsForRelease = selectHasFeatureWorkItemsForStepRunId(state, stepRunId);
    if (hasFeatureWorkItemsForRelease) {
        const workingBranchItems = selectAllWorkingBranchWorkItems(state, stepRunId);
        const workingBranchPRs = workingBranchItems.filter(
            (item) => !!item?.code && !codeIsCommit(item.code) && item.code.status === FeatureItemCodeStatus.OpenPR,
        );

        return workingBranchPRs;
    }

    return [];
};

export const selectAddFixRequestsToOpenPRsAgainstReleaseBranches =
    (appId: string) =>
    (state: RootState): boolean => {
        const appSettings = selectAppSettings(state, appId);
        return appSettings.addFixRequestsToOpenPRsAgainstReleaseBranches;
    };

export const selectRequiresFixRequestsApprovals =
    (appId: string) =>
    (state: RootState): boolean => {
        const appSettings = selectAppSettings(state, appId);
        return appSettings.requiresFixRequestsApprovals;
    };

export const selectRequiredFixRequestsApprovals =
    (appId: string) =>
    (state: RootState): number => {
        const appSettings = selectAppSettings(state, appId);
        return appSettings.requiredFixRequestsApprovals;
    };

export const selectFixRequestFormFieldsSettings = (appId: string) => (state: RootState) => {
    const appSettings = selectAppSettings(state, appId);
    return appSettings.fixRequestFormFieldsSettings ?? [];
};

export const selectBundleSizeDeviceModelId =
    (appId: string) =>
    (state: RootState): string => {
        const appSettings = selectAppSettings(state, appId);
        return appSettings.bundleSizeDeviceModelId ?? '';
    };

export const selectASCDeviceModels =
    (appId: string) =>
    (state: RootState): { id: string; displayName: string }[] => {
        const appSettings = selectAppSettings(state, appId);
        return appSettings.ascDeviceModels ?? [];
    };

export const selectFixRequestsAutoMergeUserCreatedPRsSettings =
    (appId: string) =>
    (
        state: RootState,
    ): {
        mergeUserCreatedPRs: boolean;
        workingBranchUserCreatedPRMergeType: MergeType;
        releaseBranchUserCreatedPRMergeType: MergeType;
    } => {
        const appSettings = selectAppSettings(state, appId);
        return {
            mergeUserCreatedPRs: !!appSettings.mergeUserCreatedPRs,
            workingBranchUserCreatedPRMergeType:
                appSettings.workingBranchUserCreatedPRMergeType || MergeType.DoNotMerge,
            releaseBranchUserCreatedPRMergeType:
                appSettings.releaseBranchUserCreatedPRMergeType || MergeType.DoNotMerge,
        };
    };

export const selectVCSSupportsStatusChecks =
    (appId: string, orgId: string) =>
    (state: RootState): boolean => {
        const vcsIntegration = selectVCSIntegration(state, appId);
        if (!vcsIntegration) return false;
        const vcsProvider = selectProviderForIntegration(state, orgId, vcsIntegration);

        if (vcsProvider) {
            return vcsProvider.data.supportsStatusChecks;
        }

        return false;
    };

export const selectEnableStatusChecksForFixRequests =
    (appId: string) =>
    (state: RootState): boolean => {
        const appSettings = selectAppSettings(state, appId);
        return appSettings.enableStatusChecksForFixRequests;
    };

export const selectThreadFixNotifications =
    (appId: string) =>
    (state: RootState): boolean => {
        const appSettings = selectAppSettings(state, appId);
        return appSettings.threadFixNotifications;
    };

export const selectPreventFixRequestsAfterSubmission =
    (appId: string) =>
    (state: RootState): boolean => {
        const appSettings = selectAppSettings(state, appId);
        return appSettings.preventFixRequestsAfterSubmission;
    };

export const selectPreventFixRequestsAfterSubmissionCustomCopyEnabled =
    (appId: string) =>
    (state: RootState): boolean => {
        const appSettings = selectAppSettings(state, appId);
        return appSettings.preventFixRequestsAfterSubmissionCustomCopyEnabled;
    };

export const selectPreventFixRequestsAfterSubmissionCustomCopy =
    (appId: string) =>
    (state: RootState): string => {
        const appSettings = selectAppSettings(state, appId);
        return appSettings.preventFixRequestsAfterSubmissionCustomCopy;
    };

export const selectUserNameFromUserId = (state: RootState, orgId: string, userId: string) => {
    const orgUser = selectOrgUser(state, orgId, userId);
    return orgUser ? displayUserNameLong(orgUser) : undefined;
};

export const selectShouldShowFixesOnly: (state: RootState) => boolean = (state) => {
    return (state.ui as any).featureItemSearchAndFiltersAndSorts.shouldShowFixesOnly;
};

export const selectFiltersAndFixesCount: (state: RootState) => number = (state) => {
    return (
        // when user selects "show fixes only" button, the filters are updated to reflect this selection
        (state.ui as any).featureItemSearchAndFiltersAndSorts.filters.length
    );
};

export const selectShouldGroupWorkItemsByTicket: (state: RootState) => boolean = (state) => {
    return (state.ui as any).featureItemSearchAndFiltersAndSorts.shouldGroupWorkItemsByTicket;
};

export const selectAllOpenPRsAgainstReleaseBranch: (state: RootState, stepRunId: string) => FeatureItem[] = (
    state,
    stepRunId,
) => {
    const hasFeatureWorkItemsForRelease = selectHasFeatureWorkItemsForStepRunId(state, stepRunId);
    if (hasFeatureWorkItemsForRelease) {
        const releaseBranchItems = selectAllReleaseBranchWorkItems(state, stepRunId);
        const releaseBranchPRs = releaseBranchItems.filter(
            (item) => !!item?.code && !codeIsCommit(item.code) && item.code.status === FeatureItemCodeStatus.OpenPR,
        );

        return releaseBranchPRs;
    }

    return [];
};

export const selectAllTicketOnlyItems: (state: RootState, stepRunId: string) => FeatureItem[] = (state, stepRunId) => {
    const hasFeatureWorkItemsForRelease = selectHasFeatureWorkItemsForStepRunId(state, stepRunId);
    if (hasFeatureWorkItemsForRelease) {
        const workItems = selectFeatureWorkItemsForStepRunId(state, stepRunId);
        const uniqueTicketItems = new Map<string, FeatureItem>();

        Object.values(workItems.byId).forEach((item) => {
            if (!!item.ticket && !item.code && item.ticket?.identifier) {
                uniqueTicketItems.set(item.ticket.identifier, item);
            }
        });

        Object.values(workItems.workingBranchItemsById).forEach((item) => {
            if (!!item.ticket && !item.code && item.ticket?.identifier) {
                uniqueTicketItems.set(item.ticket.identifier, item);
            }
        });

        return Array.from(uniqueTicketItems.values()).sort((a, b) =>
            a.ticket?.identifier && b.ticket?.identifier ? a.ticket.identifier.localeCompare(b.ticket.identifier) : 0,
        );
    }

    return [];
};

export const selectOrgGroups: (state: RootState, orgId: string) => Record<string, UserGroup> = (state, orgId) => {
    const orgSettings = selectOrgSettings(state, orgId);
    return orgSettings.groups;
};

export const selectShouldShowSimplifiedBdUIForUser = (state: RootState): boolean => {
    return state.user.shouldShowSimplifiedBuildDistributionUI ?? false;
};

export const selectGroupMembers = (state: RootState, groupId: string) => {
    return selectOrg(state)!.orgSettings.groups[groupId]?.members ?? [];
};

export const selectShouldShowAutomationsTimelineEventsOnly: (state: RootState) => boolean = (state) => {
    return (state.ui as any).timelineEventFilters.shouldShowAutomationsTimelineEventsOnly;
};

export const selectReleaseTimelineEventsHistory: (
    state: RootState,
    releaseId: string,
) => TimelineEvent[] | undefined = (state, releaseId) => {
    const timelineEventsHistory = selectRelease(state, releaseId).timelineEventsHistory;
    if (timelineEventsHistory) {
        timelineEventsHistory.sort((a, b) => {
            const dateA = dayjs(a.triggeredAt);
            const dateB = dayjs(b.triggeredAt);
            return dateB.unix() - dateA.unix();
        });
    }
    return timelineEventsHistory;
};

export const selectTimelineEventsFilters: (state: RootState) => AnyTimelineEventsFilter[] = (state) => {
    return (state.ui as any).timelineEventFilters.filters;
};

export const selectTimelineEventsFiltersMap: (state: RootState) => Dictionary<AnyTimelineEventsFilter[]> = (state) => {
    const filters = selectTimelineEventsFilters(state);
    return groupBy(filters, (filter) => filter.type);
};

export const selectAppliedTimelineEventFilters: (state: RootState) => AnyTimelineEventsFilter[] = (state) => {
    const filters = selectTimelineEventsFilters(state);
    const appliedFilters = filters.filter((filter) => {
        switch (filter.type) {
            case TimelineEventsFilterType.Date:
            case TimelineEventsFilterType.ReleaseStep:
            case TimelineEventsFilterType.Automation:
            case TimelineEventsFilterType.Version:
            case TimelineEventsFilterType.App:
            case TimelineEventsFilterType.Destination:
                return true;
            default:
                return false;
        }
    });

    return appliedFilters;
};

export const selectAppliedTimelineEventFiltersCount: (state: RootState) => number = (state) => {
    const filters = selectAppliedTimelineEventFilters(state);
    const filtersKeyedByType = keyBy(filters, (filter) => filter.type);
    return Object.keys(filtersKeyedByType).length;
};

export const selectConnectedIntegrationsForStepConfig: (
    state: RootState,
    appId: string,
    stepConfigId: string,
) => Integration[] = (state, appId, stepConfigId) => {
    const stepConfig = selectStepConfig(state, stepConfigId);
    if (!stepConfig) return [];

    const integrations = selectAppIntegrations(state, appId);
    return integrations.filter((integration) =>
        stepConfig.integrationInstallationIds.includes(integration.installationId),
    );
};

export const selectOrgLevelChecklistItemTemplates: (state: RootState) => ChecklistItemTemplate[] = (state) => {
    const { entities } = state;
    return entities.orgChecklistItemTemplates;
};

export const selectOrgLevelChecklistItem: (
    state: RootState,
    checklistItemId: string,
) => ChecklistItemTemplate | undefined = (state, checklistItemId) => {
    var checklistItemTemplates = selectOrgLevelChecklistItemTemplates(state);
    return checklistItemTemplates.find((item) => item.id === checklistItemId);
};

export const selectIsStepTimelineDrawerVisible: (state: RootState, stepId: string) => boolean = (state, stepId) => {
    const drawerState = _uiConfig(state).eventsTimelineDrawer;
    return drawerState.isVisible && drawerState.stepRunId === stepId;
};

export const selectStepRunsFromSummary: (state: RootState, workflowRunId: string, stepRunIds: string[]) => StepRun[] = (
    state,
    workflowRunId,
    stepRunIds,
) => {
    const wfr = state.stepRunsSummary?.byWorkflowRunId[workflowRunId];
    if (!wfr) return [];
    const runs: StepRun[] = [];
    for (const id of stepRunIds) {
        const sr = wfr.stepRunsById[id];
        if (sr) runs.push(sr);
    }
    return runs;
};

export const selectLoadedStepRunIds: (state: RootState, workflowRunId: string) => Set<string> = (
    state,
    workflowRunId,
) => {
    const wfr = state.stepRunsSummary?.byWorkflowRunId[workflowRunId];
    if (!wfr) return new Set();
    return new Set(Object.keys(wfr.stepRunsById));
};

export const selectStepGroupAutomationRuns = (
    state: RootState,
    workflowRunId: string,
    archetype: ReleaseStepType | undefined,
): AutomationRun[] => {
    if (!archetype) return [];
    return state.stepRunsSummary?.byWorkflowRunId[workflowRunId]?.stepGroupAutomationRunsByArchetype[archetype] ?? [];
};
